import { useState, useRef, useEffect, useCallback, Fragment } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import type { DraperConversationMessage } from "@shared/schema";
import {
  Send, Loader2, Plus, Sparkles, Zap, X, Search, FolderOpen, Paperclip, Mic, MicOff,
  ChevronRight, CheckCircle2, Circle, XCircle, RefreshCw, Brain,
  Radio, Users, Target, PenTool, MessageSquare, BarChart3, Cpu, Telescope,
  Trash2, PanelRight, Clock, ChevronDown, Activity, AlertTriangle,
  LayoutGrid, TrendingUp, ExternalLink,
} from "lucide-react";
import { SiTelegram } from "react-icons/si";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useChatJobs, isActiveJob, type PendingChatJob } from "@/hooks/use-chat-jobs";
import { useWorkerStatus } from "@/hooks/use-worker-status";
import { WorkerStatusChip, WorkerDelayBanner, WorkInProgressStrip, ChatJobBubbles } from "@/components/claude-worker-ui";
import { StatusNoteCard, parseStatusNote } from "@/components/draper-status-card";
import { EscalationBanner } from "@/components/escalation-banner";
import { getAiEngine, isQueuedChatResponse } from "@/lib/ai-engine";
import { postDraperChat } from "@/lib/draper-chat";
import { takeDraperPrefill } from "@/lib/draper-prefill";
import { usePilotMode } from "@/hooks/use-pilot-mode";
import { FinishSetupCard } from "@/components/finish-setup-card";
import { publicActivityText, friendlyActivityText } from "@shared/activity-text";
import { AgentFace, type FaceMood } from "@/components/agent-face";
import { TodayWithDraper } from "@/components/today-with-draper";

type AgentVoice = "draper" | "peggy" | "pete" | "rogers" | "harry" | "claw";
type UIMode = "simple" | "power";
type ViewState = "main" | "project";

interface DraperProject {
  id: number;
  workspaceId: number;
  name: string;
  description: string;
  agentCast: string[];
  messageHistory: DraperConversationMessage[];
  createdAt: string;
  updatedAt: string;
}

interface CardActionData {
  contentId?: number;
  content?: string;
  platform?: string;
  hashtags?: string;
  [key: string]: unknown;
}

interface StreamingLogEntry {
  id: string;
  agent: string;
  action: string;
  type: string;
  step?: number;
  total?: number;
}

interface LeadItem {
  name: string;
  status: string;
}

const AGENT_META: Record<AgentVoice, { label: string; color: string; bg: string; border: string; icon: React.ElementType; dot: string }> = {
  draper: { label: "Draper", color: "text-blue-400", bg: "bg-blue-500/10", border: "border-blue-500/25", icon: Sparkles, dot: "#60a5fa" },
  peggy: { label: "Peggy", color: "text-pink-400", bg: "bg-pink-500/10", border: "border-pink-500/25", icon: PenTool, dot: "#f472b6" },
  pete: { label: "Pete", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/25", icon: Target, dot: "#fbbf24" },
  rogers: { label: "Rogers", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/25", icon: Users, dot: "#34d399" },
  harry: { label: "Harry", color: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/25", icon: Telescope, dot: "#a78bfa" },
  claw: { label: "Claw", color: "text-cyan-400", bg: "bg-cyan-500/10", border: "border-cyan-500/25", icon: Cpu, dot: "#22d3ee" },
};

const MENTION_SUGGESTIONS = ["@Draper", "@Peggy", "@Pete", "@Rogers", "@Harry", "@Claw"];

const QUICK_PROMPTS = [
  { text: "What should I focus on today?", icon: Sparkles },
  { text: "@Peggy schedule a LinkedIn post for this week", icon: PenTool },
  { text: "@Pete how is my ad spend tracking?", icon: Target },
  { text: "@Rogers who are my hottest leads right now?", icon: Users },
  { text: "Give me a full marketing performance summary", icon: BarChart3 },
  { text: "@Harry what trends should I be riding this week?", icon: Telescope },
];

const LEAD_STATUS_MAP: Record<string, string> = {
  NEW: "aware", AWARE: "aware",
  CONTACTED: "interest", INTERESTED: "interest", ENGAGED: "interest",
  QUALIFIED: "demo", DEMO: "demo", DEMO_SCHEDULED: "demo",
  CLOSING: "close", CLOSED_WON: "close", PROPOSAL: "close",
};

const QUICK_CHIPS = [
  { text: "/ commands", icon: Sparkles },
  { text: "Show my leads", icon: Users },
  { text: "Write a post", icon: PenTool },
  { text: "Check ad spend", icon: Target },
  { text: "Set up chat widget", icon: MessageSquare },
];

function SparklineSVG({ pts, color }: { pts: number[]; color: string }) {
  if (pts.length < 2) return null;
  const max = Math.max(...pts);
  const min = Math.min(...pts);
  const range = max - min || 1;
  const w = 52, h = 18;
  const step = w / (pts.length - 1);
  const coords = pts.map((p, i) =>
    `${(i * step).toFixed(1)},${(h - 2 - ((p - min) / range) * (h - 5)).toFixed(1)}`
  ).join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="opacity-60 mt-1">
      <polyline points={coords} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AgentAvatar({ voice, size = "sm", mood = "idle" }: { voice: AgentVoice; size?: "sm" | "md"; mood?: FaceMood }) {
  return <AgentFace agent={voice} mood={mood} size={size === "md" ? 36 : 30} />;
}

function RichCard({ card, onAction }: { card: NonNullable<DraperConversationMessage["richCard"]>; onAction: (action: string, data: CardActionData) => void }) {
  const [state, setState] = useState<"pending" | "approved" | "rejected" | "dismissed">(card.actionState || "pending");

  if (card.type === "post_approval") {
    return (
      <div className="mt-3 rounded-xl overflow-hidden border border-pink-500/20 bg-pink-500/5" data-testid="rich-card-post-approval">
        <div className="px-4 py-2.5 flex items-center gap-2 border-b border-pink-500/15">
          <PenTool size={11} className="text-pink-400" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-pink-400">Post Approval</span>
          <span className="ml-auto text-[10px] text-white/30">{card.data.platform || "Social"}</span>
        </div>
        <div className="px-4 py-3">
          <p className="text-sm text-white/75 leading-relaxed whitespace-pre-wrap">{card.data.content}</p>
          {card.data.hashtags && <p className="text-xs text-blue-400/60 mt-2">{card.data.hashtags}</p>}
          {card.data.qualityScore != null && (
            <div className="flex items-center gap-1.5 mt-2">
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/20 text-amber-400 font-bold">QS {card.data.qualityScore}/10</span>
            </div>
          )}
          {card.data.scheduledAt && (
            <p className="text-[11px] text-white/30 mt-1.5">Scheduled: {new Date(card.data.scheduledAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
          )}
        </div>
        {state === "pending" && (
          <div className="px-4 py-2.5 border-t border-pink-500/15 flex gap-2">
            <button onClick={() => { setState("approved"); onAction("approve", card.data); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-400 text-xs font-bold rounded-lg transition-all"
              data-testid="button-approve-post">
              <CheckCircle2 size={11} /> Schedule
            </button>
            <button onClick={() => { setState("rejected"); onAction("reject", card.data); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-400 text-xs font-bold rounded-lg transition-all"
              data-testid="button-reject-post">
              <XCircle size={11} /> Reject
            </button>
            <button onClick={() => { setState("dismissed"); onAction("edit", card.data); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/5 hover:bg-white/8 border border-white/10 text-white/40 text-xs font-medium rounded-lg transition-all ml-auto">
              Edit
            </button>
          </div>
        )}
        {state !== "pending" && (
          <div className={`px-4 py-2 border-t border-white/5 flex items-center gap-2 ${state === "approved" ? "bg-emerald-500/5" : "bg-red-500/5"}`}>
            {state === "approved" ? <CheckCircle2 size={11} className="text-emerald-400" /> : <XCircle size={11} className="text-red-400" />}
            <span className={`text-xs font-bold ${state === "approved" ? "text-emerald-400" : "text-red-400"}`}>
              {state === "approved" ? "Approved & queued" : "Rejected"}
            </span>
          </div>
        )}
      </div>
    );
  }

  if (card.type === "campaign_status") {
    return (
      <div className="mt-3 rounded-xl overflow-hidden border border-amber-500/20 bg-amber-500/5" data-testid="rich-card-campaign-status">
        <div className="px-4 py-2.5 flex items-center gap-2 border-b border-amber-500/15">
          <Target size={11} className="text-amber-400" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-400">Campaign Status</span>
        </div>
        <div className="px-4 py-3 grid grid-cols-3 gap-3">
          {[{ label: "Spend", value: card.data.spend || "$0" }, { label: "CTR", value: card.data.ctr || "—" }, { label: "ROAS", value: card.data.roas || "—" }].map(s => (
            <div key={s.label} className="text-center">
              <p className="text-base font-black text-white/90">{s.value}</p>
              <p className="text-[9px] text-white/30 uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>
        {card.data.name && <div className="px-4 pb-3 text-xs text-white/30">{card.data.name}</div>}
      </div>
    );
  }

  if (card.type === "lead_activity") {
    return (
      <div className="mt-3 rounded-xl overflow-hidden border border-emerald-500/20 bg-emerald-500/5" data-testid="rich-card-lead-activity">
        <div className="px-4 py-2.5 flex items-center gap-2 border-b border-emerald-500/15">
          <Users size={11} className="text-emerald-400" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">Lead Activity</span>
        </div>
        <div className="px-4 py-3 space-y-1.5">
          {(card.data.leads as LeadItem[] || []).slice(0, 4).map((lead, i: number) => (
            <div key={i} className="space-y-0.5">
              <div className="flex items-center gap-2 text-xs">
                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
                <span className="font-medium text-white/75">{lead.name}</span>
                <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-400/10 text-emerald-400 border border-emerald-400/20 capitalize">{lead.status}</span>
              </div>
              {(lead as any).email && (
                <div className="flex items-center gap-2 pl-3.5">
                  <span className="text-[10px] text-white/30">{(lead as any).source || 'Web'}</span>
                  {(lead as any).engagementScore > 0 && (
                    <div className="flex items-center gap-1 ml-auto">
                      <div className="h-1 rounded-full bg-white/10 w-12">
                        <div className="h-1 rounded-full bg-emerald-400/60" style={{ width: `${Math.min((lead as any).engagementScore, 100)}%` }} />
                      </div>
                      <span className="text-[9px] text-white/25">{(lead as any).engagementScore}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (card.type === "analytics_summary") {
    return (
      <div className="mt-3 rounded-xl overflow-hidden border border-blue-500/20 bg-blue-500/5" data-testid="rich-card-analytics">
        <div className="px-4 py-2.5 flex items-center gap-2 border-b border-blue-500/15">
          <BarChart3 size={11} className="text-blue-400" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400">Analytics Summary</span>
        </div>
        <div className="px-4 py-3 grid grid-cols-2 gap-3">
          {Object.entries(card.data.metrics || {}).map(([key, val]) => {
            const metricColors: Record<string, string> = { 'Published': 'text-emerald-400', 'Scheduled': 'text-blue-400', 'Pending Review': 'text-amber-400', 'Leads': 'text-purple-400', 'Campaigns': 'text-orange-400' };
            const col = metricColors[key] || 'text-white/90';
            return (
              <div key={key}>
                <p className={`text-base font-black ${col}`}>{String(val)}</p>
                <p className="text-[9px] text-white/30 uppercase tracking-wider">{key}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  if (card.type === 'post_preview') {
    const pub = card.data.publishedAt ? new Date(card.data.publishedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '';
    return (
      <div className="mt-3 rounded-xl overflow-hidden border border-purple-500/20 bg-purple-500/5" data-testid="rich-card-post-preview">
        <div className="px-4 py-2.5 flex items-center gap-2 border-b border-purple-500/15">
          <TrendingUp size={11} className="text-purple-400" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-purple-400">Published Post</span>
          <span className="ml-auto text-[10px] text-white/30">{card.data.platform}{pub ? ` · ${pub}` : ''}</span>
        </div>
        <div className="px-4 py-3">
          <p className="text-sm text-white/70 leading-relaxed line-clamp-3">{card.data.content}{(card.data.content || '').length >= 200 ? '…' : ''}</p>
        </div>
        <div className="px-4 pb-3 grid grid-cols-4 gap-2">
          {[{ label: 'Impressions', value: card.data.impressions?.toLocaleString() || '0' }, { label: 'Likes', value: card.data.likes || '0' }, { label: 'Comments', value: card.data.comments || '0' }, { label: 'Eng. Rate', value: card.data.engagementRate ? `${Number(card.data.engagementRate).toFixed(1)}%` : '0%' }].map(s => (
            <div key={s.label} className="text-center">
              <p className="text-sm font-black text-purple-300">{s.value}</p>
              <p className="text-[9px] text-white/25 uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (card.type === 'routine_confirm') {
    return (
      <div className="mt-3 rounded-xl overflow-hidden border border-indigo-500/20 bg-indigo-500/5" data-testid="rich-card-routine-confirm">
        <div className="px-4 py-2.5 flex items-center gap-2 border-b border-indigo-500/15">
          <Clock size={11} className="text-indigo-400" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-indigo-400">Schedule Routine</span>
        </div>
        <div className="px-4 py-3">
          <p className="text-sm font-bold text-white/90">{card.data.name}</p>
          <p className="text-xs text-white/50 mt-1">{card.data.description}</p>
        </div>
        {state === 'pending' && (
          <div className="px-4 pb-3 flex gap-2">
            <button
              onClick={() => { setState('approved'); onAction('routine_confirm', card.data); }}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:opacity-80 bg-emerald-500/15 border border-emerald-500/25 text-emerald-400"
            >
              Create Routine
            </button>
            <button
              onClick={() => { setState('dismissed'); onAction('routine_cancel', card.data); }}
              className="px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:opacity-80 bg-white/5 border border-white/10 text-white/40"
            >
              Cancel
            </button>
          </div>
        )}
        {state === 'approved' && (
          <div className="px-4 pb-3 flex items-center gap-2 text-emerald-400 text-[11px] font-bold">
            <CheckCircle2 size={12} /> Routine Created
          </div>
        )}
        {state === 'dismissed' && (
          <div className="px-4 pb-3 flex items-center gap-2 text-white/30 text-[11px] font-bold">
            <XCircle size={12} /> Cancelled
          </div>
        )}
      </div>
    );
  }

  if (card.type === 'routine_created') {
    const nextRun = card.data.nextRunAt ? new Date(card.data.nextRunAt).toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
    return (
      <div className="mt-3 rounded-xl overflow-hidden border border-emerald-500/20 bg-emerald-500/5" data-testid="rich-card-routine-created">
        <div className="px-4 py-2.5 flex items-center gap-2 border-b border-emerald-500/15">
          <CheckCircle2 size={11} className="text-emerald-400" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-400">Routine Active</span>
        </div>
        <div className="px-4 py-3">
          <p className="text-sm font-bold text-white/90">{card.data.name}</p>
          {nextRun && <p className="text-xs text-white/50 mt-1">Next run: {nextRun}</p>}
        </div>
        <div className="px-4 pb-3">
          <a href="/settings" className="text-[11px] text-indigo-400 hover:text-indigo-300 transition-colors">View Routines →</a>
        </div>
      </div>
    );
  }

  return null;
}

const BATCH_CARD_CONFIG = {
  content_batch: {
    color: "pink",
    icon: PenTool,
    label: "All Created Posts",
    linkLabel: "Review all in Content Studio",
  },
  campaign_batch: {
    color: "amber",
    icon: Target,
    label: "All Campaigns",
    linkLabel: "View all in Ad Campaigns",
  },
  lead_batch: {
    color: "emerald",
    icon: Users,
    label: "All Active Leads",
    linkLabel: "View all in Lead Desk",
  },
} as const;

function BatchCard({ card }: { card: NonNullable<DraperConversationMessage["batchCard"]> }) {
  const { activeWorkspaceId } = useWorkspaceStore();
  const { toast } = useToast();
  const cfg = BATCH_CARD_CONFIG[card.type];
  const Icon = cfg.icon;
  const colorMap = {
    pink: { border: "border-pink-500/20", bg: "bg-pink-500/5", divider: "border-pink-500/15", icon: "text-pink-400", dot: "bg-pink-400" },
    amber: { border: "border-amber-500/20", bg: "bg-amber-500/5", divider: "border-amber-500/15", icon: "text-amber-400", dot: "bg-amber-400" },
    emerald: { border: "border-emerald-500/20", bg: "bg-emerald-500/5", divider: "border-emerald-500/15", icon: "text-emerald-400", dot: "bg-emerald-400" },
  } as const;
  const c = colorMap[cfg.color];
  const visibleItems = card.data.items.slice(0, 6);
  const remaining = card.data.total - visibleItems.length;
  const isContentBatch = card.type === "content_batch";
  const cardTitle = isContentBatch ? `${card.data.total} Post${card.data.total !== 1 ? 's' : ''} Created` : cfg.label;
  // Historical batch cards persisted in draper_conversations.message_history baked in the old
  // /content-studio path (no such route -> 404). Normalize the legacy segment to /content at
  // render time so old dashboard cards resolve, while preserving any query string.
  const viewHref = card.data.viewUrl?.replace(/^\/content-studio(?=$|[/?#])/, "/content");
  const [approveState, setApproveState] = useState<"idle" | "loading" | "done">("idle");

  const handleApproveAll = async () => {
    if (!activeWorkspaceId || approveState !== "idle") return;
    const contentIds = card.data.items.map((item: any) => item.id).filter(Boolean);
    if (contentIds.length === 0) return;
    setApproveState("loading");
    try {
      await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/content/approve-all`, { contentIds });
      setApproveState("done");
      toast({ title: `${contentIds.length} post${contentIds.length !== 1 ? "s" : ""} approved & queued` });
    } catch {
      setApproveState("idle");
      toast({ title: "Failed to approve posts", variant: "destructive" });
    }
  };

  return (
    <div className={`mt-2 rounded-xl overflow-hidden border ${c.border} ${c.bg}`} data-testid={`batch-card-${card.type}`}>
      <div className={`px-4 py-2.5 flex items-center gap-2 border-b ${c.divider}`}>
        <Icon size={11} className={c.icon} />
        <span className={`text-[10px] font-bold uppercase tracking-wider ${c.icon}`}>{cardTitle}</span>
        {!isContentBatch && <span className="ml-auto text-[10px] text-white/30">{card.data.total} total</span>}
      </div>
      <div className="px-4 py-3 space-y-1.5">
        {visibleItems.map((item: any, i: number) => (
          <div key={item.id ?? i} className="flex items-center gap-2 text-xs">
            {approveState === "done" && isContentBatch ? (
              <CheckCircle2 size={10} className="text-emerald-400 shrink-0" />
            ) : (
              <div className={`w-1.5 h-1.5 rounded-full ${c.dot} shrink-0`} />
            )}
            <span className="font-medium text-white/80 truncate flex-1">{item.title}</span>
            <span className="text-white/30 shrink-0">{item.subtitle}</span>
          </div>
        ))}
        {remaining > 0 && (
          <p className="text-[10px] text-white/30 pl-3.5">+{remaining} more</p>
        )}
      </div>
      <div className={`px-4 py-2.5 border-t ${c.divider} flex items-center gap-3`}>
        {viewHref && (
          <a
            href={viewHref}
            className={`inline-flex items-center gap-1.5 text-[11px] font-bold ${c.icon} hover:opacity-80 transition-opacity`}
            data-testid={`batch-card-link-${card.type}`}
          >
            {cfg.linkLabel} <ExternalLink size={10} />
          </a>
        )}
        {isContentBatch && approveState !== "done" && (
          <button
            onClick={handleApproveAll}
            disabled={approveState === "loading"}
            className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all hover:opacity-80 disabled:opacity-50"
            style={{ background: "rgba(16,185,129,0.12)", border: "1px solid rgba(16,185,129,0.25)", color: "#34d399" }}
            data-testid="button-approve-all-batch"
          >
            {approveState === "loading" ? (
              <><Loader2 size={10} className="animate-spin" /> Approving…</>
            ) : (
              <><CheckCircle2 size={10} /> Approve all</>
            )}
          </button>
        )}
        {isContentBatch && approveState === "done" && (
          <span
            className="ml-auto inline-flex items-center gap-1.5 text-[11px] font-bold text-emerald-400"
            data-testid="batch-card-approved-confirmation"
          >
            <CheckCircle2 size={10} /> All approved & queued
          </span>
        )}
      </div>
    </div>
  );
}

function MorningBriefCard({ msg, onCardAction }: { msg: DraperConversationMessage; onCardAction: (action: string, data: CardActionData, ts: string) => void }) {
  const now = new Date();
  const dateStr = now.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }).toUpperCase();
  const stats = (msg.metadata?.stats as any) || null;
  const coldLeads = (msg.metadata?.coldLeads as Array<{ name: string; status: string }>) || [];

  const leadsThisWeek = stats?.leadsThisWeek ?? 0;
  const leadsWeekDelta = stats?.leadsWeekDelta ?? 0;
  const pendingContent = stats?.pendingContent ?? 0;
  const adSpend = stats?.adSpend ?? 0;
  const adBudget = stats?.adBudget ?? 0;
  const adRemaining = adBudget - adSpend;
  const leadsSparkline = (stats?.leadsSparkline as number[] | undefined) ?? null;
  const contentSparkline = (stats?.contentSparkline as number[] | undefined) ?? null;

  return (
    <div className="rounded-2xl overflow-hidden border border-blue-500/20" data-brief-card style={{ background: "linear-gradient(135deg, rgba(29,78,216,0.18) 0%, rgba(109,40,217,0.12) 50%, rgba(6,6,20,0.9) 100%)" }} data-testid={`msg-agent-${msg.ts}`}>
      {/* Header */}
      <div className="px-5 py-3.5 flex items-center justify-between gap-3 border-b border-blue-400/15" data-brief-header style={{ background: "linear-gradient(135deg, rgba(29,78,216,0.25) 0%, rgba(109,40,217,0.15) 100%)" }}>
        <div className="flex items-center gap-3 min-w-0">
          <AgentFace agent="draper" mood="happy" size={34} />
          <div className="min-w-0">
            {/* The brief is written once a day; the greeting is for when it was written */}
            <p className="text-sm font-bold text-white leading-tight">{greetingFor(new Date(msg.ts))}.</p>
            <p className="text-[10px] text-white/50">Here's your daily brief from Draper</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="hidden sm:inline px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-wider" style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)", color: "#f59e0b" }}>☀️ Daily brief</span>
          <span className="text-[10px] text-blue-300/45 font-mono">{dateStr}</span>
        </div>
      </div>

      {/* Stats row — rendered when real stats are present */}
      {stats && (
        <div className="grid grid-cols-3 divide-x" style={{ borderBottom: "1px solid rgba(59,130,246,0.1)" }}>
          {/* Leads this week */}
          <div className="px-4 py-3 flex flex-col gap-0.5" style={{ background: "rgba(29,78,216,0.07)" }}>
            <div className="text-xl font-black" style={{ color: "#60a5fa" }}>{leadsThisWeek}</div>
            <div className="text-[9px] text-white/35 uppercase tracking-wider">Leads this week</div>
            <div className={`flex items-center gap-1 text-[9px] font-bold ${leadsWeekDelta >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              <TrendingUp size={8} className={leadsWeekDelta < 0 ? "rotate-180" : ""} />
              {leadsWeekDelta >= 0 ? "+" : ""}{leadsWeekDelta} vs last week
            </div>
            <SparklineSVG pts={leadsSparkline && leadsSparkline.length >= 2 ? leadsSparkline : [Math.max(0, leadsThisWeek - Math.abs(leadsWeekDelta) * 3), Math.max(0, leadsThisWeek - Math.abs(leadsWeekDelta)), leadsThisWeek]} color="#60a5fa" />
          </div>
          {/* Posts pending */}
          <div className="px-4 py-3 flex flex-col gap-0.5" style={{ background: "rgba(109,40,217,0.07)" }}>
            <div className="text-xl font-black" style={{ color: "#a78bfa" }}>{pendingContent}</div>
            <div className="text-[9px] text-white/35 uppercase tracking-wider">Posts awaiting you</div>
            <div className={`flex items-center gap-1 text-[9px] font-bold ${pendingContent > 0 ? "text-amber-400" : "text-emerald-400"}`}>
              <Clock size={8} />
              {pendingContent > 0 ? "Needs approval" : "All clear"}
            </div>
            <SparklineSVG pts={contentSparkline && contentSparkline.length >= 2 ? contentSparkline : [0, pendingContent > 1 ? pendingContent - 1 : 0, pendingContent]} color="#a78bfa" />
          </div>
          {/* Ad spend */}
          <div className="px-4 py-3 flex flex-col gap-0.5" style={{ background: "rgba(16,185,129,0.05)" }}>
            {adBudget > 0 ? (
              <>
                <div className="text-xl font-black" style={{ color: "#34d399" }}>${adSpend.toFixed(0)}</div>
                <div className="text-[9px] text-white/35 uppercase tracking-wider">Ad spend today</div>
                <div className={`flex items-center gap-1 text-[9px] font-bold ${adRemaining >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  <TrendingUp size={8} />
                  {adRemaining >= 0 ? `$${adRemaining.toFixed(0)} under budget` : `$${Math.abs(adRemaining).toFixed(0)} over`}
                </div>
                <SparklineSVG pts={[adSpend * 0.55, adSpend * 0.75, adSpend * 0.9, adSpend || 1]} color="#34d399" />
              </>
            ) : (
              <>
                <div className="text-xl font-black text-white/20">—</div>
                <div className="text-[9px] text-white/25 uppercase tracking-wider">No active campaigns</div>
                <div className="text-[9px] text-white/20 mt-0.5">Ask Pete to launch one</div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Brief prose */}
      <div className="px-5 py-4">
        <div className="prose prose-sm prose-invert max-w-none
          prose-p:text-white/70 prose-p:leading-relaxed prose-p:my-1.5 prose-p:last:mb-0
          prose-headings:text-white/85 prose-headings:font-bold
          prose-strong:text-white/85 prose-strong:font-semibold
          prose-code:text-cyan-300 prose-code:bg-white/[0.07] prose-code:rounded prose-code:px-1 prose-code:before:content-none prose-code:after:content-none
          prose-ul:text-white/65 prose-ol:text-white/65 prose-li:text-white/65
          prose-a:text-blue-400 prose-a:no-underline
          [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
        </div>

        {/* Cold leads observation */}
        {coldLeads.length > 0 && (
          <div className="mt-4 rounded-xl overflow-hidden" style={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.15)" }}>
            <div className="px-4 py-3 flex items-start gap-3">
              <span className="text-base shrink-0">🧊</span>
              <div className="flex-1 min-w-0">
                <p className="text-[12px] text-white/70 leading-relaxed">
                  <strong className="text-white/85">{coldLeads.length} lead{coldLeads.length !== 1 ? "s" : ""} {coldLeads.length === 1 ? "has" : "have"} gone cold</strong>
                  {" — "}
                  {coldLeads.map((l, i) => (
                    <span key={i}>
                      <span className="text-blue-300 font-semibold">{l.name.split(" ")[0]}</span>
                      {i < coldLeads.length - 1 ? ", " : ""}
                    </span>
                  ))}.{" "}Want Rogers to reach out?
                </p>
                <button
                  onClick={() => onCardAction("rogers_outreach", { leadNames: coldLeads.map(l => l.name) }, msg.ts)}
                  className="mt-2.5 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition-all hover:opacity-80"
                  style={{ background: "rgba(52,211,153,0.12)", border: "1px solid rgba(52,211,153,0.25)", color: "#34d399" }}
                  data-testid="button-rogers-outreach"
                >
                  <Users size={10} /> Have Rogers reach out
                </button>
              </div>
            </div>
          </div>
        )}

        {msg.richCard && (
          <div className="mt-3">
            <RichCard card={msg.richCard} onAction={(action, data) => onCardAction(action, data, msg.ts)} />
          </div>
        )}
        {msg.batchCard && <BatchCard card={msg.batchCard} />}
      </div>
    </div>
  );
}

/** "Good morning" / "Good afternoon" / "Good evening" for a time on the viewer's clock. */
function greetingFor(d: Date): string {
  const h = isNaN(d.getTime()) ? new Date().getHours() : d.getHours();
  return h < 5 ? "Good evening" : h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
}

function getDateLabel(ts: string): string {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '';
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (d.toDateString() === today.toDateString()) return 'Today';
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return d.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
  } catch { return ''; }
}

function DateSeparator({ dateLabel }: { dateLabel: string }) {
  if (!dateLabel) return null;
  return (
    <div className="flex items-center gap-3 py-2 px-1" data-testid="date-separator">
      <div className="flex-1 h-px" style={{ background: 'var(--chat-surface-border, rgba(255,255,255,0.06))' }} />
      <span className="text-[10px] text-white/25 font-medium tracking-wider uppercase shrink-0">{dateLabel}</span>
      <div className="flex-1 h-px" style={{ background: 'var(--chat-surface-border, rgba(255,255,255,0.06))' }} />
    </div>
  );
}

function MessageBubble({ msg, onCardAction, streamedText, isFailed, errorMsg, onRetry }: {
  msg: DraperConversationMessage;
  onCardAction: (action: string, data: CardActionData, ts: string) => void;
  streamedText?: string;
  isFailed?: boolean;
  errorMsg?: string;
  onRetry?: () => void;
}) {
  const isUser = msg.role === "user";
  const voice = (msg.agentVoice || "draper") as AgentVoice;
  const meta = AGENT_META[voice] ?? AGENT_META.draper;
  const isTelegram = msg.surface === "telegram";
  const isMorningBrief = !!msg.metadata?.isMorningBrief;
  const isAutonomous = msg.surface === "autonomous";

  if (isMorningBrief && !isUser) {
    return <MorningBriefCard msg={msg} onCardAction={onCardAction} />;
  }

  // Status notes from the Claude team and the autopublisher (⏳ / ✅ / ⚠️ ...) are cards with a "View" link, not chat bubbles.
  const statusNote = !isUser && streamedText === undefined ? parseStatusNote(msg) : null;
  if (statusNote) {
    return (
      <div className="flex gap-2.5" data-testid={`msg-agent-${msg.ts}`} data-kind="status-note">
        <AgentAvatar voice={voice} />
        <div className="max-w-[80%] flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <span className={`text-[10px] font-bold uppercase tracking-wider ${meta.color}`}>{meta.label}</span>
            <span className="text-[9px] text-white/20 ml-auto">{new Date(msg.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
          </div>
          <StatusNoteCard note={statusNote} />
        </div>
      </div>
    );
  }

  if (isUser) {
    return (
      <div className="flex justify-end gap-2.5" data-testid={`msg-user-${msg.ts}`}>
        <div className="max-w-[78%]">
          {isTelegram && (
            <div className="flex items-center gap-1 mb-1 justify-end text-sky-400/50 text-[9px]">
              <SiTelegram size={8} /><span>via Telegram</span>
            </div>
          )}
          <div style={{ background: "var(--chat-user-bubble-bg)", border: "1px solid var(--chat-user-bubble-border)", color: "var(--chat-user-bubble-text, inherit)" }} className="rounded-2xl rounded-tr-sm px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap">
            {msg.text}
          </div>
          {isFailed && (
            <div className='flex items-center gap-2 mt-1.5 justify-end' data-testid='msg-error-indicator'>
              <AlertTriangle size={10} className='text-red-400/80 shrink-0' />
              <span className='text-[11px] text-red-400/70'>{errorMsg || 'Failed to send'}</span>
              <button
                onClick={onRetry}
                className='flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-bold bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-all'
                data-testid='button-retry-message'
              >
                <RefreshCw size={9} /> Retry
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2.5" data-testid={`msg-agent-${msg.ts}`}>
      <AgentAvatar voice={voice} />
      <div className="max-w-[80%] flex-1">
        <div className="flex items-center gap-2 mb-1.5">
          <span className={`text-[10px] font-bold uppercase tracking-wider ${meta.color}`}>{meta.label}</span>
          {isAutonomous && !isMorningBrief && (
            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-[9px] font-bold">
              <Radio size={7} /> Autonomous
            </span>
          )}
          {isTelegram && (
            <span className="flex items-center gap-1 text-sky-400/50 text-[9px]">
              <SiTelegram size={7} /> Telegram
            </span>
          )}
          <span className="text-[9px] text-white/20 ml-auto">{new Date(msg.ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
        </div>
        <div className={`${meta.bg} ${meta.border} border rounded-2xl rounded-tl-sm px-4 py-3`}>
          <div className="prose prose-sm prose-invert max-w-none
            prose-p:text-white/75 prose-p:leading-relaxed prose-p:my-1.5 prose-p:last:mb-0
            prose-headings:text-white/90 prose-headings:font-bold prose-headings:leading-tight prose-headings:mt-3 prose-headings:mb-1
            prose-h1:text-base prose-h2:text-[15px] prose-h3:text-sm prose-h4:text-[13px]
            prose-strong:text-white/90 prose-strong:font-semibold
            prose-code:text-cyan-300 prose-code:bg-white/[0.07] prose-code:rounded prose-code:px-1 prose-code:py-0.5 prose-code:text-[12px] prose-code:font-mono prose-code:before:content-none prose-code:after:content-none
            prose-pre:bg-black/50 prose-pre:rounded-xl prose-pre:text-[11px] prose-pre:border prose-pre:border-white/10
            prose-ul:text-white/70 prose-ul:my-1.5 prose-ol:text-white/70 prose-ol:my-1.5
            prose-li:text-white/70 prose-li:my-0.5 prose-li:leading-relaxed
            prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline prose-a:font-normal
            prose-blockquote:border-l-blue-500/40 prose-blockquote:text-white/50 prose-blockquote:not-italic prose-blockquote:bg-white/[0.03] prose-blockquote:rounded-r-lg prose-blockquote:py-0.5
            prose-hr:border-white/10 prose-hr:my-3
            [&>*:first-child]:mt-0 [&>*:last-child]:mb-0">
            {streamedText !== undefined ? (
              <span className="text-white/75 text-sm leading-relaxed whitespace-pre-wrap">
                {streamedText}
                <span className="inline-block w-[2px] h-[1em] bg-cyan-400/80 ml-[2px] align-middle animate-pulse" />
              </span>
            ) : (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
            )}
          </div>
          {msg.richCard && (
            <div className="mt-3">
              <RichCard card={msg.richCard} onAction={(action, data) => onCardAction(action, data, msg.ts)} />
            </div>
          )}
          {msg.batchCard && <BatchCard card={msg.batchCard} />}
        </div>
      </div>
    </div>
  );
}

function ContentCalendarPanel({ wsId }: { wsId: number }) {
  const { data: items = [] } = useQuery<any[]>({
    queryKey: ["/api/workspaces", wsId, "content"],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${wsId}/content`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 60000,
  });

  const days = ["M", "T", "W", "T", "F", "S", "S"];
  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - (now.getDay() || 7) + 1);
  weekStart.setHours(0, 0, 0, 0);

  const scheduled = items.filter((it: any) => it.scheduledAt && it.status !== "PUBLISHED");
  const weekItems = scheduled.filter((it: any) => {
    const d = new Date(it.scheduledAt);
    return d >= weekStart && d < new Date(weekStart.getTime() + 7 * 86400000);
  });

  const dayHasPosts = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart.getTime() + i * 86400000);
    return weekItems.some((it: any) => {
      const id = new Date(it.scheduledAt);
      return id.toDateString() === d.toDateString();
    });
  });

  const pending = items.filter((it: any) => it.approvalStatus === "pending_approval").length;
  const total = scheduled.length;

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
      <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="w-2 h-2 rounded-full bg-indigo-400" />
        <span className="text-xs font-bold text-white/80">Content Calendar</span>
        <span className="ml-auto text-[10px] text-white/30">{total} scheduled</span>
      </div>
      <div className="px-4 py-3 space-y-3">
        <div className="flex gap-1.5">
          {days.map((d, i) => {
            const isToday = i === ((now.getDay() + 6) % 7);
            const hasPost = dayHasPosts[i];
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <span className={`text-[9px] font-bold ${isToday ? "text-blue-400" : "text-white/25"}`}>{d}</span>
                <div className={`w-6 h-6 rounded-md flex items-center justify-center text-[9px] font-bold transition-all ${
                  isToday ? "bg-blue-500/25 border border-blue-400/40 text-blue-300" :
                  hasPost ? "bg-white/8 border border-white/15 text-white/50" :
                  "border border-white/8 text-white/15"
                }`}>
                  {hasPost ? "●" : "·"}
                </div>
              </div>
            );
          })}
        </div>
        {pending > 0 && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/8 border border-amber-500/20">
            <Clock size={11} className="text-amber-400" />
            <span className="text-[11px] text-amber-300/80">{pending} post{pending !== 1 ? "s" : ""} awaiting approval</span>
          </div>
        )}
        {total === 0 && (
          <p className="text-[11px] text-white/25 text-center py-1">No posts scheduled — ask Peggy to fill the calendar</p>
        )}
      </div>
    </div>
  );
}

function LeadPipelinePanel({ wsId }: { wsId: number }) {
  const { data: leadsData = [] } = useQuery<any[]>({
    queryKey: ["/api/workspaces", wsId, "leads"],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${wsId}/leads`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 60000,
  });

  const counts = { aware: 0, interest: 0, demo: 0, close: 0 };
  leadsData.forEach((l: any) => {
    const stage = LEAD_STATUS_MAP[l.status] || "aware";
    counts[stage as keyof typeof counts]++;
  });
  const total = leadsData.length;
  const hot = leadsData.filter((l: any) => ["QUALIFIED", "DEMO", "DEMO_SCHEDULED", "CLOSING"].includes(l.status)).length;

  const stages = [
    { key: "aware", label: "Aware", color: "#818cf8", bg: "rgba(99,102,241,0.25)" },
    { key: "interest", label: "Interest", color: "#34d399", bg: "rgba(52,211,153,0.25)" },
    { key: "demo", label: "Demo", color: "#60a5fa", bg: "rgba(96,165,250,0.25)" },
    { key: "close", label: "Close", color: "#f59e0b", bg: "rgba(245,158,11,0.25)" },
  ];

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
      <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="w-2 h-2 rounded-full bg-emerald-400" />
        <span className="text-xs font-bold text-white/80">Lead Pipeline</span>
        <span className="ml-auto text-[10px] text-white/30">{total} active{hot > 0 ? ` · ${hot} hot 🔥` : ""}</span>
      </div>
      <div className="px-4 py-3 space-y-2.5">
        {stages.map(s => {
          const count = counts[s.key as keyof typeof counts];
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={s.key} className="flex items-center gap-2.5">
              <span className="text-[10px] text-white/40 w-14 shrink-0">{s.label}</span>
              <div className="flex-1 h-1.5 rounded-full bg-white/5 overflow-hidden">
                <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: s.color, opacity: 0.8 }} />
              </div>
              <span className="text-[10px] text-white/50 w-5 text-right shrink-0">{count}</span>
            </div>
          );
        })}
        {total === 0 && (
          <p className="text-[11px] text-white/25 text-center py-1">No leads yet — Rogers is ready to help</p>
        )}
      </div>
    </div>
  );
}

function AdSpendPanel({ wsId }: { wsId: number }) {
  const { data: envelopes = [] } = useQuery<any[]>({
    queryKey: ["/api/workspaces", wsId, "ad-budget-envelopes"],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${wsId}/ad-budget-envelopes`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 60000,
  });

  const totalBudget = envelopes.reduce((a: number, e: any) => a + (e.weeklyLimitCents || 0) / 100, 0);
  const totalSpent = envelopes.reduce((a: number, e: any) => a + (e.currentWeekSpentCents || 0) / 100, 0);
  const remaining = totalBudget - totalSpent;
  const pct = totalBudget > 0 ? Math.min(100, Math.round((totalSpent / totalBudget) * 100)) : 0;
  const isOver = totalSpent > totalBudget;
  const isGood = pct < 85;

  return (
    <div className="rounded-xl overflow-hidden" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
      <div className="px-4 py-3 flex items-center gap-2 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className={`w-2 h-2 rounded-full ${totalBudget > 0 ? (isGood ? "bg-amber-400" : "bg-red-400") : "bg-white/15"}`} />
        <span className="text-xs font-bold text-white/80">Ad Spend</span>
        <span className="ml-auto text-[10px] text-white/30">{envelopes.length} envelope{envelopes.length !== 1 ? "s" : ""}</span>
      </div>
      <div className="px-4 py-3 space-y-3">
        {totalBudget > 0 ? (
          <>
            <div className="flex items-end justify-between">
              <div>
                <p className="text-xl font-black text-white/90">${totalSpent.toFixed(0)}</p>
                <p className="text-[10px] text-white/30">of ${totalBudget.toFixed(0)} weekly budget</p>
              </div>
              <div className="text-right">
                <p className={`text-sm font-bold ${isOver ? "text-red-400" : "text-emerald-400"}`}>
                  {isOver ? `-$${Math.abs(remaining).toFixed(0)} over` : `$${remaining.toFixed(0)} left`}
                </p>
              </div>
            </div>
            <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
              <div className="h-full rounded-full transition-all duration-700" style={{
                width: `${Math.min(100, pct)}%`,
                background: isOver ? "#f87171" : isGood ? "#34d399" : "#fbbf24"
              }} />
            </div>
            <p className={`text-[11px] ${isGood ? "text-emerald-400/70" : isOver ? "text-red-400/70" : "text-amber-400/70"}`}>
              {isOver ? "Over budget — Pete should review" : isGood ? `Pete is pacing well · ${pct}% used` : `Approaching limit · ${pct}% used`}
            </p>
          </>
        ) : (
          <p className="text-[11px] text-white/25 text-center py-1">No active campaigns — ask Pete to set one up</p>
        )}
      </div>
    </div>
  );
}

interface FeedEntry {
  kind: "agent_log" | "pending_approval" | "agent_run";
  ts: string;
  agentLog?: {
    id: number;
    agent: string;
    action: string;
    type: string;
    status: string;
    details?: string;
    metadata?: Record<string, unknown>;
    createdAt: string;
  };
  pendingContent?: {
    id: number;
    caption?: string;
    platform?: string;
    contentType?: string;
    approvalStatus: string;
    status: string;
    createdAt: string;
    scheduledAt?: string;
    mediaUrl?: string;
  };
  agentRun?: {
    id: number | string;
    agent: string;
    trigger: string;
    status: string;
    actionsCount?: number;
    totalCost?: number;
    summary?: string;
    startedAt: string;
    completedAt?: string;
  };
}

function timeAgo(ts: string | null | undefined): string {
  if (!ts) return "never";
  try {
    const diff = Date.now() - new Date(ts).getTime();
    if (isNaN(diff)) return "recently";
    const mins = Math.floor(diff / 60000);
    const hrs = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    if (hrs < 24) return `${hrs}h ago`;
    return `${days}d ago`;
  } catch {
    return "recently";
  }
}

function AgentLogEntry({ entry }: { entry: FeedEntry }) {
  const log = entry.agentLog!;
  const agent = (log.agent || "draper").toLowerCase() as AgentVoice;
  const meta = AGENT_META[agent] || AGENT_META.draper;
  const isAuto = log.type === "thought" || log.type === "action";

  return (
    <div className="flex gap-2.5 group" data-testid={`activity-log-${log.id}`}>
      <AgentFace agent={agent} size={26} className="mt-0.5" mood={entry.agentRun?.status === "running" ? "thinking" : "idle"} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
          <span className={`text-[10px] font-bold ${meta.color}`}>{meta.label}</span>
          {isAuto && (
            <span className="flex items-center gap-0.5 px-1 py-0.5 rounded bg-blue-500/10 border border-blue-500/20 text-blue-400/80 text-[9px] font-bold">
              <Radio size={7} /> auto
            </span>
          )}
          {log.status === "failed" && (
            <span className="flex items-center gap-0.5 px-1 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-red-400/80 text-[9px] font-bold">
              <AlertTriangle size={7} /> failed
            </span>
          )}
          <span className="text-[9px] text-white/25 ml-auto shrink-0">{timeAgo(entry.ts)}</span>
        </div>
        <p className="text-xs text-white/60 leading-relaxed line-clamp-4">{friendlyActivityText(log.action) ?? log.action}</p>
        {log.details && !String(log.action).includes(log.details.slice(0, 40)) && (
          <p className="text-[10px] text-white/30 mt-0.5 truncate">{log.details}</p>
        )}
      </div>
    </div>
  );
}

function AgentRunEntry({ entry }: { entry: FeedEntry }) {
  const run = entry.agentRun!;
  const [expanded, setExpanded] = useState(false);
  const agent = (run.agent || "draper").toLowerCase() as AgentVoice;
  const meta = AGENT_META[agent] || AGENT_META.draper;

  const durationSec = run.startedAt && run.completedAt
    ? Math.round((new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime()) / 1000)
    : null;

  const durationLabel = durationSec !== null
    ? durationSec < 60 ? `${durationSec}s` : `${Math.round(durationSec / 60)}m`
    : run.status === "running" ? "running" : null;

  return (
    <div className="flex gap-2.5 group" data-testid={`activity-run-${run.id}`}>
      <AgentFace agent={agent} size={26} className="mt-0.5" mood={entry.agentRun?.status === "running" ? "thinking" : "idle"} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
          <span className={`text-[10px] font-bold ${meta.color}`}>{meta.label}</span>
          <span className="flex items-center gap-0.5 px-1 py-0.5 rounded bg-purple-500/10 border border-purple-500/20 text-purple-400/80 text-[9px] font-bold">
            <Zap size={7} /> run
          </span>
          {run.status === "running" && (
            <span className="flex items-center gap-0.5 px-1 py-0.5 rounded bg-blue-500/10 border border-blue-500/20 text-blue-400/80 text-[9px] font-bold" data-testid={`badge-run-running-${run.id}`}>
              <Loader2 size={7} className="animate-spin" /> running
            </span>
          )}
          {run.status === "failed" && (
            <span className="flex items-center gap-0.5 px-1 py-0.5 rounded bg-red-500/10 border border-red-500/20 text-red-400/80 text-[9px] font-bold">
              <AlertTriangle size={7} /> failed
            </span>
          )}
          {run.status === "completed" && (
            <span className="flex items-center gap-0.5 px-1 py-0.5 rounded bg-green-500/10 border border-green-500/20 text-green-400/80 text-[9px] font-bold">
              <CheckCircle2 size={7} /> done
            </span>
          )}
          {durationLabel && (
            <span className="text-[9px] text-white/30">{durationLabel}</span>
          )}
          <span className="text-[9px] text-white/25 ml-auto shrink-0">{timeAgo(entry.ts)}</span>
        </div>
        <button
          onClick={() => setExpanded(e => !e)}
          className="text-xs text-white/60 leading-relaxed text-left w-full hover:text-white/80 transition-colors"
          data-testid={`button-expand-run-${run.id}`}
        >
          {run.status === "running" ? `${meta.label} is on a task you asked for` : `${meta.label} worked on a task you asked for`}
          {run.actionsCount ? ` · ${run.actionsCount} steps` : ""}
          {run.summary ? (expanded ? " ▲" : " ▼") : ""}
        </button>
        {expanded && run.summary && (
          <p className="text-[10px] text-white/35 mt-1 leading-relaxed whitespace-pre-wrap">{run.summary}</p>
        )}
      </div>
    </div>
  );
}

function PendingApprovalEntry({
  entry, wsId, onDecision,
}: {
  entry: FeedEntry;
  wsId: number;
  onDecision: (contentId: number, decision: "approved" | "rejected") => void;
}) {
  const content = entry.pendingContent!;
  const [state, setState] = useState<"pending" | "approved" | "rejected">("pending");
  const { toast } = useToast();

  const decide = useMutation({
    mutationFn: async (decision: "approved" | "rejected") => {
      const res = await apiRequest("POST", `/api/workspaces/${wsId}/activity-feed/content/${content.id}/decision`, { decision });
      return res.json();
    },
    onSuccess: (_data, decision) => {
      setState(decision);
      onDecision(content.id, decision);
    },
    onError: () => toast({ title: "Action failed", description: "Could not process decision.", variant: "destructive" }),
  });

  const platformColor: Record<string, string> = {
    linkedin: "text-blue-400", twitter: "text-sky-400", instagram: "text-pink-400",
    facebook: "text-blue-500", youtube: "text-red-400",
  };
  const pColor = platformColor[(content.platform || "").toLowerCase()] || "text-pink-400";

  return (
    <div
      className="rounded-xl overflow-hidden border border-amber-500/20 bg-amber-500/5"
      data-testid={`activity-pending-${content.id}`}
    >
      <div className="px-3 py-2 flex items-center gap-2 border-b border-amber-500/15">
        <Clock size={10} className="text-amber-400" />
        <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">Waiting for your OK</span>
        <span className={`ml-auto text-[9px] font-bold ${pColor}`}>{content.platform || "Post"}</span>
        <span className="text-[9px] text-white/25">{timeAgo(entry.ts)}</span>
      </div>
      {content.caption && (
        <div className="px-3 py-2">
          <p className="text-[11px] text-white/65 leading-relaxed line-clamp-3">{content.caption}</p>
          {content.contentType && (
            <p className="text-[10px] text-white/35 mt-0.5 font-medium uppercase">{content.contentType}</p>
          )}
        </div>
      )}
      {state === "pending" ? (
        <div className="px-3 py-2 border-t border-amber-500/15 flex gap-1.5">
          <button
            onClick={() => decide.mutate("approved")}
            disabled={decide.isPending}
            data-testid={`button-approve-feed-${content.id}`}
            className="flex items-center gap-1 px-2.5 py-1 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-400 text-[10px] font-bold rounded-lg transition-all disabled:opacity-50"
          >
            {decide.isPending ? <Loader2 size={9} className="animate-spin" /> : <CheckCircle2 size={9} />}
            Approve
          </button>
          <button
            onClick={() => decide.mutate("rejected")}
            disabled={decide.isPending}
            data-testid={`button-reject-feed-${content.id}`}
            className="flex items-center gap-1 px-2.5 py-1 bg-red-500/15 hover:bg-red-500/25 border border-red-500/30 text-red-400 text-[10px] font-bold rounded-lg transition-all disabled:opacity-50"
          >
            <XCircle size={9} />
            Reject
          </button>
        </div>
      ) : (
        <div className={`px-3 py-1.5 border-t border-white/5 flex items-center gap-1.5 ${state === "approved" ? "bg-emerald-500/5" : "bg-red-500/5"}`}>
          {state === "approved"
            ? <><CheckCircle2 size={9} className="text-emerald-400" /><span className="text-[10px] font-bold text-emerald-400">Approved & scheduled</span></>
            : <><XCircle size={9} className="text-red-400" /><span className="text-[10px] font-bold text-red-400">Rejected</span></>
          }
        </div>
      )}
    </div>
  );
}

type FeedFilter = "all" | "runs" | "logs" | "approvals";

const FEED_FILTERS: { key: FeedFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "runs", label: "Runs" },
  { key: "logs", label: "Logs" },
  { key: "approvals", label: "Approvals" },
];

function ActivityFeedPanel({ wsId }: { wsId: number }) {
  const queryClient = useQueryClient();
  const [localFeed, setLocalFeed] = useState<FeedEntry[]>([]);
  const [decisionIds, setDecisionIds] = useState<Set<number>>(new Set());
  const [activeFilter, setActiveFilter] = useState<FeedFilter>(() => { const s = localStorage.getItem("drpr-activity-filter") as FeedFilter; return (["all","runs","logs","approvals"] as const).includes(s) ? s : "all"; });

  const { data, isLoading } = useQuery<{ feed: FeedEntry[]; pendingCount: number }>({
    queryKey: ["/api/workspaces", wsId, "activity-feed"],
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${wsId}/activity-feed`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    staleTime: 30000,
    refetchInterval: 60000,
    enabled: !!wsId,
  });

  useEffect(() => {
    if (data?.feed) {
      setLocalFeed(prev => {
        // Preserve ephemeral "running" entries not yet in the persisted feed
        // so a periodic refetch doesn't wipe an in-progress run from the UI
        const ephemeralRunning = prev.filter(
          e =>
            e.kind === "agent_run" &&
            e.agentRun?.status === "running" &&
            !data.feed.some(f => f.kind === "agent_run" && f.agentRun?.id === e.agentRun?.id)
        );
        return [...ephemeralRunning, ...data.feed];
      });
    }
  }, [data]);

  // SSE for real-time activity updates
  useEffect(() => {
    const es = new EventSource(`/api/workspaces/${wsId}/draper-stream`, { withCredentials: true });
    es.onmessage = (event) => {
      try {
        const payload = JSON.parse(event.data);
        if (payload.type === "activity_event" && payload.event) {
          const ev = payload.event;

          if (ev.kind === "agent_run_start") {
            // Add a live "running" agent run entry immediately
            const runEntry: FeedEntry = {
              kind: "agent_run",
              ts: ev.startedAt || new Date().toISOString(),
              agentRun: {
                id: ev.id,
                agent: ev.agent,
                trigger: ev.trigger || "chat",
                status: "running",
                startedAt: ev.startedAt || new Date().toISOString(),
              },
            };
            setLocalFeed(prev => {
              if (prev.some(e => e.agentRun?.id === ev.id)) return prev;
              return [runEntry, ...prev].slice(0, 100);
            });
            return;
          }

          if (ev.kind === "agent_run_complete") {
            // Update the existing running entry in-place, or insert if not present
            setLocalFeed(prev => {
              const idx = prev.findIndex(e => e.agentRun?.id === ev.id);
              const completedEntry: FeedEntry = {
                kind: "agent_run",
                ts: ev.startedAt || new Date().toISOString(),
                agentRun: {
                  id: ev.id,
                  agent: ev.agent,
                  trigger: ev.trigger || "chat",
                  status: ev.status || "completed",
                  startedAt: ev.startedAt || new Date().toISOString(),
                  completedAt: ev.completedAt,
                  summary: ev.summary,
                },
              };
              if (idx !== -1) {
                const next = [...prev];
                next[idx] = completedEntry;
                return next;
              }
              return [completedEntry, ...prev].slice(0, 100);
            });
            return;
          }

          // Default: agent_log entry
          const newEntry: FeedEntry = {
            kind: "agent_log",
            ts: ev.createdAt || new Date().toISOString(),
            agentLog: {
              id: ev.id || Date.now(),
              agent: ev.agent,
              action: ev.action,
              type: ev.type || "thought",
              status: ev.status || "completed",
              details: ev.details,
              metadata: ev.metadata,
              createdAt: ev.createdAt || new Date().toISOString(),
            },
          };
          setLocalFeed(prev => {
            // Deduplicate by id
            if (ev.id && prev.some(e => e.agentLog?.id === ev.id)) return prev;
            return [newEntry, ...prev].slice(0, 100);
          });
        } else if (payload.type === "content_decision" && payload.contentId) {
          // Remove decided items from feed
          setDecisionIds(prev => new Set([...prev, payload.contentId]));
          queryClient.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "activity-feed"] });
        }
      } catch {}
    };
    return () => es.close();
  }, [wsId, queryClient]);

  const handleDecision = (contentId: number, _decision: "approved" | "rejected") => {
    setDecisionIds(prev => new Set([...prev, contentId]));
    queryClient.invalidateQueries({ queryKey: ["/api/workspaces", wsId, "activity-feed"] });
  };

  const decidedFeed = localFeed.filter(e =>
    e.kind !== "pending_approval" || !decisionIds.has(e.pendingContent!.id)
  );

  const visibleFeed = decidedFeed.filter(e => {
    if (activeFilter === "all") return true;
    if (activeFilter === "runs") return e.kind === "agent_run";
    if (activeFilter === "logs") return e.kind === "agent_log";
    if (activeFilter === "approvals") return e.kind === "pending_approval";
    return true;
  });

  const pendingCount = decidedFeed.filter(e => e.kind === "pending_approval").length;
  const runsCount = decidedFeed.filter(e => e.kind === "agent_run").length;
  const logsCount = decidedFeed.filter(e => e.kind === "agent_log").length;

  return (
    <div className="flex flex-col h-full" data-testid="activity-feed-panel">
      <div className="flex items-center gap-2 pl-4 pr-12 sm:pr-4 py-3 border-b shrink-0" style={{ borderColor: "var(--chat-surface-border)" }}>
        <Activity size={12} className="text-white/40" />
        <span className="text-[10px] font-bold uppercase tracking-widest text-white/30">What the team did</span>
        {pendingCount > 0 && (
          <span className="ml-auto flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/25 text-amber-400 text-[9px] font-bold" data-testid="badge-pending-count">
            <Clock size={8} /> {pendingCount} pending
          </span>
        )}
      </div>
      <div className="flex items-center gap-1 px-3 pt-2.5 pb-1 shrink-0 flex-wrap">
        {FEED_FILTERS.map(f => (
          <button
            key={f.key}
            onClick={() => { setActiveFilter(f.key); localStorage.setItem("drpr-activity-filter", f.key); }}
            data-testid={`filter-feed-${f.key}`}
            className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border transition-all ${
              activeFilter === f.key
                ? "bg-white/10 border-white/20 text-white"
                : "bg-transparent border-white/10 text-white/35 hover:text-white/60 hover:border-white/20"
            }`}
          >
            {f.label}
            {f.key === "runs" && runsCount > 0 && (
              <span className="ml-1 text-white/50">{runsCount}</span>
            )}
            {f.key === "logs" && logsCount > 0 && (
              <span className="ml-1 text-white/50">{logsCount}</span>
            )}
            {f.key === "approvals" && pendingCount > 0 && (
              <span className="ml-1 text-amber-400">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar px-3 py-3 space-y-3">
        {isLoading && (
          <div className="flex justify-center py-8">
            <Loader2 size={16} className="animate-spin text-white/20" />
          </div>
        )}
        {!isLoading && visibleFeed.length === 0 && (
          <div className="flex flex-col items-center justify-center py-10 text-center gap-2">
            <AgentFace agent="draper" size={40} />
            <p className="text-[11px] text-white/40 max-w-48 leading-relaxed" data-testid="text-feed-empty">
              {activeFilter === "all"
                ? "Nothing yet. When the team does something for you, I'll note it here."
                : activeFilter === "approvals"
                  ? "Nothing waiting on you. I'll put anything that needs your OK right here."
                  : `No ${activeFilter} to show yet.`}
            </p>
          </div>
        )}
        {visibleFeed.map((entry, i) =>
          entry.kind === "pending_approval" ? (
            <PendingApprovalEntry
              key={`pending-${entry.pendingContent!.id}`}
              entry={entry}
              wsId={wsId}
              onDecision={handleDecision}
            />
          ) : entry.kind === "agent_run" ? (
            <AgentRunEntry key={`run-${entry.agentRun!.id}-${i}`} entry={entry} />
          ) : (
            <AgentLogEntry key={`log-${entry.agentLog!.id}-${i}`} entry={entry} />
          )
        )}
      </div>
    </div>
  );
}

function CreateProjectModal({ onClose, onCreated }: { onClose: () => void; onCreated: (p: DraperProject) => void }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedAgents, setSelectedAgents] = useState<AgentVoice[]>(["draper"]);
  const { activeWorkspaceId } = useWorkspaceStore();

  const create = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/draper-projects`, { name: name.trim(), description, agentCast: selectedAgents });
      return res.json();
    },
    onSuccess: onCreated,
  });

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="modal-create-project">
        <div className="rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-5" style={{ background: "rgba(12,12,24,0.98)", border: "1px solid rgba(255,255,255,0.1)" }}>
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-white">New Project</h3>
            <button onClick={onClose} className="text-white/30 hover:text-white/60" data-testid="button-close-modal"><X size={16} /></button>
          </div>
          <input
            value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Q2 Launch, Blog Strategy"
            className="w-full text-sm bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-white/25 outline-none focus:border-blue-500/40"
            data-testid="input-project-name"
          />
          <textarea
            value={description} onChange={e => setDescription(e.target.value)} placeholder="What's the goal? (optional)"
            rows={2} className="w-full text-sm bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-white/25 outline-none focus:border-blue-500/40 resize-none"
            data-testid="input-project-description"
          />
          <div>
            <p className="text-[10px] text-white/35 uppercase tracking-wider font-bold mb-2">Agent Team</p>
            <div className="flex flex-wrap gap-2">
              {(["draper", "peggy", "pete", "rogers", "harry"] as AgentVoice[]).map(a => {
                const m = AGENT_META[a] ?? AGENT_META.draper; const Icon = m.icon;
                const sel = selectedAgents.includes(a);
                return (
                  <button key={a} onClick={() => setSelectedAgents(prev => sel ? prev.filter(x => x !== a) : [...prev, a])}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all ${sel ? `${m.bg} ${m.border} ${m.color}` : "bg-white/5 border-white/10 text-white/40"}`}
                    data-testid={`button-agent-${a}`}>
                    <Icon size={11} />{m.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-white/10 text-white/40 text-sm hover:bg-white/5 transition-all">Cancel</button>
            <button onClick={() => create.mutate()} disabled={!name.trim() || create.isPending}
              className="flex-1 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-sm font-bold transition-all flex items-center justify-center gap-2"
              data-testid="button-create-project">
              {create.isPending ? <Loader2 size={14} className="animate-spin" /> : null} Create
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function InlineThinkingStream({ logs, sending, recentMessages = [] }: { logs: StreamingLogEntry[]; sending: boolean; recentMessages?: DraperConversationMessage[] }) {
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const shownLogs = logs.slice(-6);
  const latest = shownLogs[shownLogs.length - 1];
  const older = shownLogs.slice(0, -1);

  const activeAgent = (latest?.agent?.toLowerCase() || "draper") as AgentVoice;
  const meta = AGENT_META[activeAgent] || AGENT_META.draper;
  const latestStep = [...logs].reverse().find(l => l.step)?.step;
  const latestTotal = [...logs].reverse().find(l => l.total)?.total;
  const handbackLog = shownLogs.find(l => l.type === 'handback');

  if (handbackLog && !sending) {
    const hv = (handbackLog.agent?.toLowerCase() || 'draper') as AgentVoice;
    const hm = AGENT_META[hv] || AGENT_META.draper;
    return (
      <div className="flex gap-2.5 animate-in fade-in duration-300">
        <AgentAvatar voice={hv} mood="happy" />
        <div className="max-w-[82%] flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <span className={`text-[10px] font-bold uppercase tracking-wider ${hm.color}`}>{hm.label}</span>
            <span className="text-[9px] px-1.5 py-0.5 rounded-full border font-bold" style={{ background: 'rgba(52,211,153,0.08)', borderColor: 'rgba(52,211,153,0.25)', color: '#34d399' }}>Done</span>
          </div>
          <div className="bg-emerald-500/10 border border-emerald-500/25 rounded-2xl rounded-tl-sm px-4 py-3 flex items-start gap-2.5">
            <CheckCircle2 size={13} className="text-emerald-400 shrink-0 mt-0.5" />
            <span className="text-sm text-white/75 leading-snug">{handbackLog.action}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-2.5 animate-in fade-in duration-300">
      <AgentAvatar voice={activeAgent} mood="thinking" />
      <div className="max-w-[82%] flex-1">
        <div className="flex items-center gap-2 mb-1.5">
          <span className={`text-[10px] font-bold uppercase tracking-wider ${meta.color}`}>{meta.label}</span>
          {sending && (
            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-bold" style={{ background: "rgba(34,211,238,0.06)", borderColor: "rgba(34,211,238,0.2)", color: "#22d3ee" }}>
              <Radio size={7} className="animate-pulse" /> Working
            </span>
          )}
          {latestStep && latestTotal && (
            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded border" style={{ background: "rgba(99,102,241,0.08)", borderColor: "rgba(99,102,241,0.2)", color: "#a5b4fc" }}>Step {latestStep}/{latestTotal}</span>
          )}
        </div>
        {recentMessages.length > 0 && sending && (
          <div className="mt-1.5 mb-2">
            <button
              onClick={() => setTranscriptOpen(o => !o)}
              className="flex items-center gap-1.5 text-[10px] text-white/25 hover:text-white/45 transition-colors"
              data-testid="button-toggle-transcript"
            >
              <ChevronDown size={9} className={`transition-transform duration-150 ${transcriptOpen ? '' : '-rotate-90'}`} />
              Context · {recentMessages.length} message{recentMessages.length !== 1 ? 's' : ''}
            </button>
            {transcriptOpen && (
              <div className="mt-1.5 rounded-xl overflow-hidden border border-white/[0.06] bg-white/[0.02]" data-testid="panel-transcript">
                {recentMessages.map((m, i) => {
                  const v = (m.agentVoice || (m.role === 'user' ? undefined : 'draper')) as AgentVoice | undefined;
                  const label = m.role === 'user' ? 'You' : (v ? (AGENT_META[v]?.label ?? 'Draper') : 'Draper');
                  const col = m.role === 'user' ? 'text-white/40' : (v ? (AGENT_META[v]?.color ?? 'text-blue-400') : 'text-blue-400');
                  return (
                    <div key={i} className="px-3 py-1.5 flex items-start gap-2 border-b border-white/[0.04] last:border-b-0">
                      <span className={`text-[9px] font-bold uppercase tracking-wider shrink-0 pt-0.5 w-10 truncate ${col}`}>{label}</span>
                      <span className="text-[10px] text-white/25 leading-snug line-clamp-2 flex-1">{m.text}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <div className={`${meta.bg} ${meta.border} border rounded-2xl rounded-tl-sm overflow-hidden`}>
          {older.length > 0 && older.map((log, i) => {
            const v = (log.agent?.toLowerCase() || "draper") as AgentVoice;
            const m = AGENT_META[v] || AGENT_META.draper;
            return (
              <div key={`${log.id}-${i}`} className="px-4 py-1.5 flex items-center gap-2 border-b border-white/[0.05]">
                <span className="w-1.5 h-1.5 rounded-full shrink-0 opacity-40" style={{ background: m.dot }} />
                <span className={`text-[10px] font-bold shrink-0 opacity-50 ${m.color}`}>{log.agent}</span>
                <span className="text-[11px] text-white/25 truncate flex-1">{log.action}</span>
                <CheckCircle2 size={9} className="text-emerald-400/40 shrink-0" />
              </div>
            );
          })}
          <div className="px-4 py-3 flex items-start gap-2.5">
            {sending ? (
              <Loader2 size={12} className="animate-spin text-blue-400 shrink-0 mt-0.5" />
            ) : (
              <CheckCircle2 size={12} className="text-emerald-400/60 shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              {latest ? (
                <span className="text-sm text-white/65 leading-snug">{latest.action}</span>
              ) : (
                <span className="text-sm text-white/30">Agents are working...</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DraperHome() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  // #11: the badge shows the user's real plan (users.subscription: free / solo / growth ...), and nothing while PILOT_MODE hides pricing
  const pilotMode = usePilotMode();
  const planLabel = String((user as any)?.subscription || "free").toUpperCase();

  const [uiMode, setUiMode] = useState<UIMode>(() => (localStorage.getItem("drpr-ui-mode") as UIMode) || "simple");
  const [showActivity, setShowActivity] = useState(false);
  const [viewState, setViewState] = useState<ViewState>("main");
  const [activeProjectId, setActiveProjectId] = useState<number | null>(null);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [showProjectsList, setShowProjectsList] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');

  const [draperMessages, setDraperMessages] = useState<DraperConversationMessage[]>([]);
  const [projectMessages, setProjectMessages] = useState<DraperConversationMessage[]>([]);
  const [sending, setSending] = useState(false);
  const [cardStates, setCardStates] = useState<Record<string, "pending" | "approved" | "rejected" | "dismissed">>({});
  const [briefLoading, setBriefLoading] = useState(false);
  const [input, setInput] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [draperMode, setDraperMode] = useState<"basic" | "power">("basic");
  const [telegramConnected, setTelegramConnected] = useState(false);
  const [streamingLogs, setStreamingLogs] = useState<StreamingLogEntry[]>([]);
  const [agentWorking, setAgentWorking] = useState(false);
  const [showIdleCursor, setShowIdleCursor] = useState(false);
  const [streamingMsgTs, setStreamingMsgTs] = useState<string | null>(null);
  const [streamedWordCount, setStreamedWordCount] = useState(0);
  const [failedMsgs, setFailedMsgs] = useState<Record<string, string>>({});

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  // Text handed over by another page (e.g. Content Studio "Ask Draper"): fill the box, never auto-send.
  const prefillRef = useRef<string | null>(null);
  useEffect(() => {
    const prefill = prefillRef.current ?? takeDraperPrefill(); // ref survives the dev double-mount
    prefillRef.current = prefill;
    if (!prefill) return;
    setInput(prefill);
    const t = setTimeout(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(prefill.length, prefill.length);
    }, 150);
    return () => clearTimeout(t);
  }, []);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streamTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const agentWorkingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [sttLoading, setSttLoading] = useState(false);

  const startStreaming = useCallback((text: string, ts: string) => {
    if (streamTimerRef.current) clearInterval(streamTimerRef.current);
    const words = text.split(/\s+/).filter(Boolean);
    if (words.length === 0) return;
    setStreamingMsgTs(ts);
    setStreamedWordCount(0);
    let count = 0;
    const msPerWord = Math.max(14, Math.min(50, 3200 / words.length));
    streamTimerRef.current = setInterval(() => {
      count++;
      setStreamedWordCount(count);
      if (count >= words.length) {
        clearInterval(streamTimerRef.current!);
        streamTimerRef.current = null;
        setTimeout(() => { setStreamingMsgTs(null); setStreamedWordCount(0); }, 150);
      }
    }, msPerWord);
  }, []);

  const activeProject = viewState === "project" && activeProjectId ? activeProjectId : null;
  const [historySearch, setHistorySearch] = useState('');
  const [showHistorySearch, setShowHistorySearch] = useState(false);
  const rawMessages = viewState === "project" ? projectMessages : draperMessages;
  const displayMessages = historySearch.trim()
    ? rawMessages.filter(m => typeof m.text === 'string' && m.text.toLowerCase().includes(historySearch.toLowerCase()))
    : rawMessages;
  useEffect(() => { setHistorySearch(''); setShowHistorySearch(false); }, [viewState]);
  useEffect(() => { if (!showProjectsList) setProjectSearch(''); }, [showProjectsList]);

  // Load workspace details
  const { data: workspaces = [] } = useQuery<any[]>({
    queryKey: ["/api/workspaces"],
    queryFn: async () => {
      const res = await fetch("/api/workspaces", { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    staleTime: 300000,
  });
  const activeWs = workspaces.find((w: any) => w.id === activeWorkspaceId);

  // ── Claude worker engine ──
  // When the workspace engine is claude_worker a chat POST returns {queued, jobId, conversationId}
  // instead of a reply. We track the job, show a "working" bubble and poll until it's done.
  const aiEngine = getAiEngine(activeWs);
  const chatJobs = useChatJobs({
    storageKey: activeWorkspaceId ? `drpr-chat-jobs-${activeWorkspaceId}` : null,
    onJobDone: (job) => {
      // The worker already appended Draper's reply server-side. Refetch so it renders
      // (the draper-stream SSE may have delivered it first — the refetch just re-syncs).
      const projectId = job.scope.startsWith("project:") ? Number(job.scope.slice("project:".length)) : null;
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "draper-projects", projectId, "conversation"] });
      } else {
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "draper-conversation"] });
      }
    },
  });

  const sendText = useCallback(async (text: string, optimisticTs: string) => {
    try {
      const { res, viaWorker } = await postDraperChat({
        workspaceId: activeWorkspaceId!,
        engine: aiEngine,
        message: text,
        projectId: activeProject,
        unifiedBody: { message: text, projectId: activeProject, powerMode: draperMode === "power" },
      });
      if (!res.ok) {
        let errMsg = "Something went wrong. Please try again.";
        try {
          const errData = await res.json();
          if (res.status === 403 && errData.error === "AI disabled") errMsg = "AI is off — enable it via /saas-admin.";
          else if (res.status === 402) errMsg = "AI budget exceeded. Contact your admin.";
          else if (errData.message) errMsg = errData.message;
        } catch {}
        toast({ title: "Couldn't send", description: errMsg, variant: "destructive" });
        setFailedMsgs(prev => ({ ...prev, [optimisticTs]: errMsg }));
        return;
      }
      const data = await res.json();
      if (isQueuedChatResponse(data)) {
        // Claude worker engine: the reply lands asynchronously — track the job instead of waiting.
        chatJobs.trackJob({
          jobId: data.jobId,
          conversationId: data.conversationId,
          scope: activeProject ? `project:${activeProject}` : "main",
          messageText: text,
        });
        return;
      }
      if (viaWorker) {
        // We sent to the worker route but the server answered in-process: the engine was changed after
        // this page loaded. Refresh it and let the user resend instead of silently losing the reply.
        queryClient.invalidateQueries({ queryKey: ["/api/workspaces"] });
        const errMsg = "This workspace's AI engine changed — please resend your message.";
        toast({ title: "AI engine changed", description: errMsg, variant: "destructive" });
        setFailedMsgs(prev => ({ ...prev, [optimisticTs]: errMsg }));
        return;
      }
      if (data.agentMessage && viewState === "project") {
        setProjectMessages(prev => {
          const without = prev.filter(m => m.ts !== optimisticTs);
          return [...without, data.userMessage, data.agentMessage];
        });
      }
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "draper-conversation"] });
      // Hybrid: the instant Gemini reply may have handed work to the Claude team. The job exists by now,
      // so refresh the worker counts and the "Work in progress" strip appears without waiting for the next poll.
      if (aiEngine === "hybrid") queryClient.invalidateQueries({ queryKey: ["/api/workers/status"] });
    } catch {
      const errMsg = "Network error — check your connection.";
      toast({ title: "Network error", description: "Check your connection.", variant: "destructive" });
      setFailedMsgs(prev => ({ ...prev, [optimisticTs]: errMsg }));
    }
  }, [activeWorkspaceId, activeProject, draperMode, queryClient, toast, viewState, chatJobs.trackJob, aiEngine]);

  const filteredMentions = MENTION_SUGGESTIONS.filter(m => m.toLowerCase().includes(mentionFilter.toLowerCase()));

  function toggleUiMode() {
    const next: UIMode = uiMode === "simple" ? "power" : "simple";
    setUiMode(next);
    localStorage.setItem("drpr-ui-mode", next);
  }

  // Query pending content count for the activity badge
  const { data: activityData } = useQuery<{ feed: FeedEntry[]; pendingCount: number }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "activity-feed"],
    queryFn: async () => {
      if (!activeWorkspaceId) return { feed: [], pendingCount: 0 };
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/activity-feed`, { credentials: "include" });
      if (!res.ok) return { feed: [], pendingCount: 0 };
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    staleTime: 30000,
    refetchInterval: 60000,
  });
  const pendingCount = activityData?.pendingCount ?? 0;

  // Load schedule config for draperMode (basic/power)
  const { data: scheduleConfig } = useQuery({
    queryKey: ["/api/workspaces", activeWorkspaceId, "schedule-config"],
    queryFn: async () => {
      if (!activeWorkspaceId) return null;
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/schedule-config`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    staleTime: 60000,
  });

  useEffect(() => {
    if (scheduleConfig) {
      setDraperMode((scheduleConfig.autoGeneration?.draperMode as "basic" | "power") || "basic");
    }
  }, [scheduleConfig]);

  const workerStatus = useWorkerStatus(activeWorkspaceId, aiEngine);
  // Hybrid: when the Claude team finishes an item, re-sync the thread (the "finished" note) and the review inbox.
  const prevWorkActiveRef = useRef(0);
  useEffect(() => {
    const prev = prevWorkActiveRef.current;
    prevWorkActiveRef.current = workerStatus.active;
    if (aiEngine !== "hybrid" || !activeWorkspaceId || workerStatus.active >= prev) return;
    queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "draper-conversation"] });
    queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "content"] });
    queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "activity-feed"] });
  }, [workerStatus.active, aiEngine, activeWorkspaceId, queryClient]);
  const threadScope = viewState === "project" && activeProjectId ? `project:${activeProjectId}` : "main";
  const threadJobs = chatJobs.jobs.filter(j => j.scope === threadScope);
  const hasActiveJob = threadJobs.some(isActiveJob);

  // Draper's face: thinking while he works, a short happy hop when a new reply lands
  const [justReplied, setJustReplied] = useState(false);
  const lastAgentTsRef = useRef<string | null>(null);
  useEffect(() => {
    const last = [...draperMessages].reverse().find(m => m.role !== "user");
    if (!last) return;
    const prev = lastAgentTsRef.current;
    lastAgentTsRef.current = last.ts;
    if (!prev || prev === last.ts) return;
    setJustReplied(true);
    const t = setTimeout(() => setJustReplied(false), 4000);
    return () => clearTimeout(t);
  }, [draperMessages]);
  const draperMood: FaceMood = sending || hasActiveJob ? "thinking" : justReplied ? "happy" : "idle";

  // Load conversation
  const { data: conversationData, isLoading: loadingConversation } = useQuery({
    queryKey: ["/api/workspaces", activeWorkspaceId, "draper-conversation"],
    queryFn: async () => {
      if (!activeWorkspaceId) return { messages: [], telegramConnected: false };
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/draper-conversation`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed");
      return res.json();
    },
    enabled: !!activeWorkspaceId && viewState === "main",
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (conversationData) {
      setDraperMessages((conversationData as any).messages || []);
      setTelegramConnected((conversationData as any).telegramConnected || false);
    }
  }, [conversationData]);

  // Load projects
  const { data: projectsData = [] } = useQuery<DraperProject[]>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "draper-projects"],
    queryFn: async () => {
      if (!activeWorkspaceId) return [];
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/draper-projects`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
    enabled: !!activeWorkspaceId,
  });

  const filteredProjects = projectSearch.trim()
    ? projectsData.filter(p => p.name.toLowerCase().includes(projectSearch.toLowerCase()))
    : projectsData;

  // Load knowledge refresh time
  const { data: knowledgeRefresh } = useQuery<{ lastRefreshedAt: string | null }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "knowledge-refresh"],
    queryFn: async () => {
      if (!activeWorkspaceId) return { lastRefreshedAt: null };
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/knowledge-refresh`, { credentials: "include" });
      if (!res.ok) return { lastRefreshedAt: null };
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    refetchInterval: 300000,
  });

  const { data: dailyActivity } = useQuery<{
    agentActions: number;
    byAgent: Record<string, number>;
    contentCreated: number;
    leadsCaptured: number;
    emailsSent: number;
  }>({
    queryKey: ["/api/workspaces", activeWorkspaceId, "daily-activity"],
    queryFn: async () => {
      if (!activeWorkspaceId) return null;
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/daily-activity`, { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!activeWorkspaceId,
    staleTime: 60_000,
  });

  // Load project conversation
  const { data: projectConvData, isLoading: loadingProjectConv } = useQuery({
    queryKey: ["/api/workspaces", activeWorkspaceId, "draper-projects", activeProject, "conversation"],
    queryFn: async () => {
      if (!activeWorkspaceId || !activeProject) return { messages: [] };
      const res = await fetch(`/api/workspaces/${activeWorkspaceId}/draper-projects/${activeProject}/conversation`, { credentials: "include" });
      if (!res.ok) return { messages: [] };
      return res.json();
    },
    enabled: !!activeWorkspaceId && !!activeProject,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (projectConvData) setProjectMessages((projectConvData as any).messages || []);
  }, [projectConvData]);

  // SSE subscription
  useEffect(() => {
    if (!activeWorkspaceId) return;
    const es = new EventSource(`/api/workspaces/${activeWorkspaceId}/draper-stream`, { withCredentials: true });
    es.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === "message" && data.message) {
          // A reply landing while a queued job is pending: confirm the job now instead of waiting for the next poll.
          if (data.message.role !== "user") void chatJobs.pollNow();
          if (viewState !== "main") return;
          setDraperMessages(prev => {
            if (prev.some(m => m.ts === data.message.ts)) return prev;
            if (data.message.role === "user") {
              const recentWindow = Date.now() - 15000;
              const idx = prev.findLastIndex(m => m.role === "user" && m.text === data.message.text && new Date(m.ts).getTime() > recentWindow);
              if (idx !== -1) { const next = [...prev]; next[idx] = data.message; return next; }
            }
            if (data.message.role !== "user" && data.message.text) {
              startStreaming(data.message.text, data.message.ts);
            }
            return [...prev, data.message];
          });
        } else if (data.type === "project_message") {
          if (data.message?.role !== "user") void chatJobs.pollNow();
          if (viewState !== "project" || data.projectId !== activeProjectId) return;
          setProjectMessages(prev => {
            if (prev.some(m => m.ts === data.message.ts)) return prev;
            if (data.message.role !== "user" && data.message.text) {
              startStreaming(data.message.text, data.message.ts);
            }
            return [...prev, data.message];
          });
        } else if (data.type === "cleared") {
          if (viewState === "main") setDraperMessages([]);
        } else if (data.type === "card_action") {
          setCardStates(prev => ({ ...prev, [data.messageTs]: data.action === "approve" ? "approved" : data.action === "reject" ? "rejected" : "dismissed" }));
        } else if (data.type === "activity_event" && data.event) {
          // the server already hides runtime nudges and raw tool calls; this keeps an older server's stream clean too
          const shown = data.event.kind ? (data.event.action || "") : publicActivityText(data.event.action);
          if (shown === null) return;
          setStreamingLogs(prev => [...prev.slice(-7), {
            id: String(data.event.id || Date.now()),
            agent: data.event.agent || "Draper",
            action: shown,
            type: data.event.type || "thought",
            step: data.event.metadata?.step,
            total: data.event.metadata?.total,
          }]);
          setAgentWorking(true);
          if (agentWorkingTimerRef.current) clearTimeout(agentWorkingTimerRef.current);
          agentWorkingTimerRef.current = setTimeout(() => {
            setAgentWorking(false);
            setStreamingLogs([]);
          }, 3000);
        }
      } catch {}
    };
    return () => es.close();
  }, [activeWorkspaceId, viewState, activeProjectId]);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [displayMessages, sending, briefLoading, streamingLogs, chatJobs.jobs]);

  // Clear streaming logs after response arrives
  useEffect(() => {
    if (!sending) {
      const t = setTimeout(() => setStreamingLogs([]), 1400);
      return () => clearTimeout(t);
    }
  }, [sending]);

  // Idle cursor — show "Draper is watching..." after 3s of inactivity
  useEffect(() => {
    if (sending) {
      setShowIdleCursor(false);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      return;
    }
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => setShowIdleCursor(true), 3000);
    return () => { if (idleTimerRef.current) clearTimeout(idleTimerRef.current); };
  }, [sending, displayMessages]);

  useEffect(() => () => { if (idleTimerRef.current) clearTimeout(idleTimerRef.current); }, []);


  // Morning brief
  useEffect(() => {
    if (!activeWorkspaceId || viewState !== "main" || briefLoading) return;
    const today = new Date().toDateString();
    if (localStorage.getItem(`draper-brief-${activeWorkspaceId}`) === today) return;
    setBriefLoading(true);
    fetch(`/api/workspaces/${activeWorkspaceId}/draper-morning-brief`, { method: "POST", credentials: "include" })
      .then(r => r.json()).then(data => {
        if (data.ok && !data.skipped) localStorage.setItem(`draper-brief-${activeWorkspaceId}`, today);
      }).catch(() => {}).finally(() => setBriefLoading(false));
  }, [activeWorkspaceId, viewState]);

  const handleMicClick = useCallback(async () => {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunksRef.current = [];
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : 'audio/webm';
      const recorder = new MediaRecorder(stream, { mimeType });
      recorder.ondataavailable = (e: BlobEvent) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setIsRecording(false);
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const arrayBuf = await blob.arrayBuffer();
        const bytes = new Uint8Array(arrayBuf);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
        const base64 = btoa(binary);
        setSttLoading(true);
        try {
          const res = await apiRequest('POST', '/api/voice/stt', { audio: base64, workspaceId: activeWorkspaceId });
          const data = await res.json();
          if (data.transcript) setInput(prev => (prev ? prev + ' ' : '') + data.transcript);
        } catch { /* silent — mic transcription failed, user can retype */ }
        finally { setSttLoading(false); inputRef.current?.focus(); }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch { /* mic permission denied or not available */ }
  }, [isRecording, activeWorkspaceId]);

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || sending || !activeWorkspaceId) return;
    setSending(true);
    setInput("");
    setShowMentions(false);

    const optimisticUser: DraperConversationMessage = { role: "user", text: trimmed, surface: "browser", ts: new Date().toISOString() };
    if (viewState === "project") setProjectMessages(prev => [...prev, optimisticUser]);
    else setDraperMessages(prev => [...prev, optimisticUser]);

    try {
      await sendText(trimmed, optimisticUser.ts);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [input, sending, activeWorkspaceId, viewState, sendText]);

  const handleRetry = useCallback(async (ts: string, text: string) => {
    setFailedMsgs(prev => { const n = { ...prev }; delete n[ts]; return n; });
    const removeMsg = (prev: DraperConversationMessage[]) => prev.filter(m => m.ts !== ts);
    if (viewState === "project") setProjectMessages(removeMsg); else setDraperMessages(removeMsg);
    setSending(true);
    const newTs = new Date().toISOString();
    const newOptimistic: DraperConversationMessage = { role: "user", text, surface: "browser", ts: newTs };
    if (viewState === "project") setProjectMessages(prev => [...prev, newOptimistic]);
    else setDraperMessages(prev => [...prev, newOptimistic]);
    try {
      await sendText(text, newTs);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [viewState, sendText]);

  // Retry a queued message that failed or timed out: resend the same text as a new message.
  const handleRetryJob = useCallback(async (job: PendingChatJob) => {
    chatJobs.dismissJob(job.jobId);
    setSending(true);
    const newTs = new Date().toISOString();
    const optimistic: DraperConversationMessage = { role: "user", text: job.messageText, surface: "browser", ts: newTs };
    if (viewState === "project") setProjectMessages(prev => [...prev, optimistic]);
    else setDraperMessages(prev => [...prev, optimistic]);
    try {
      await sendText(job.messageText, newTs);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  }, [chatJobs.dismissJob, viewState, sendText]);

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    const atIdx = val.lastIndexOf("@");
    if (atIdx !== -1 && atIdx >= val.length - 10) {
      setMentionFilter(val.slice(atIdx + 1));
      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
    if (e.key === "Escape") setShowMentions(false);
  };

  const insertMention = (mention: string) => {
    const atIdx = input.lastIndexOf("@");
    setInput(atIdx !== -1 ? input.slice(0, atIdx) + mention + " " : input + mention + " ");
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const handleCardAction = async (action: string, data: CardActionData, ts: string) => {
    if (!activeWorkspaceId) return;

    // rogers_outreach: directly send a delegation message to Draper/Rogers
    if (action === "rogers_outreach") {
      const names = Array.isArray(data.leadNames) ? (data.leadNames as string[]).join(", ") : "cold leads";
      const msg = `@Rogers please reach out to these leads who have gone cold: ${names}. Draft personalized re-engagement messages for each and let me know when they're sent.`;
      setSending(true);
      const optimistic: DraperConversationMessage = { role: "user", text: msg, surface: "browser", ts: new Date().toISOString() };
      setDraperMessages(prev => [...prev, optimistic]);
      try {
        const { res } = await postDraperChat({
          workspaceId: activeWorkspaceId,
          engine: aiEngine,
          message: msg,
          unifiedBody: { message: msg, powerMode: draperMode === "power" },
        });
        const queued: unknown = res.ok ? await res.json().catch(() => null) : null;
        if (isQueuedChatResponse(queued)) {
          chatJobs.trackJob({ jobId: queued.jobId, conversationId: queued.conversationId, scope: "main", messageText: msg });
        } else {
          queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "draper-conversation"] });
        }
      } catch {} finally { setSending(false); }
      return;
    }

    if (action === 'routine_confirm') {
      setCardStates(prev => ({ ...prev, [ts]: 'approved' }));
      const resp = await fetch(`/api/workspaces/${activeWorkspaceId}/draper-routine-confirm`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ routineData: data, messageTs: ts }),
      });
      if (resp.ok) {
        const j = await resp.json();
        toast({ title: 'Routine created', description: `"${j.routine?.name || data.name}" is now active.` });
      }
      return;
    }

    if (action === 'routine_cancel') {
      setCardStates(prev => ({ ...prev, [ts]: 'dismissed' }));
      try { await apiRequest('POST', `/api/workspaces/${activeWorkspaceId}/draper-card-action`, { action: 'dismiss', data, messageTs: ts }); } catch {}
      return;
    }

    setCardStates(prev => ({ ...prev, [ts]: action === "approve" ? "approved" : action === "reject" ? "rejected" : "dismissed" }));
    try {
      await apiRequest("POST", `/api/workspaces/${activeWorkspaceId}/draper-card-action`, {
        action, data, messageTs: ts,
        ...(viewState === "project" && activeProjectId ? { projectId: activeProjectId } : {}),
      });
    } catch {}
  };

  const deleteProject = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/workspaces/${activeWorkspaceId}/draper-projects/${id}`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "draper-projects"] });
      if (viewState === "project") { setViewState("main"); setActiveProjectId(null); }
    },
  });

  const handleFileAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const content = reader.result as string;
      setInput(prev => prev + (prev ? "\n\n" : "") + `[Attached: ${file.name}]\n${content.slice(0, 2000)}`);
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const openProject = (p: DraperProject) => { setActiveProjectId(p.id); setViewState("project"); setShowProjectsList(false); };
  const activeProjectData = projectsData.find(p => p.id === activeProjectId);
  const isLoading = viewState === "main" ? loadingConversation : loadingProjectConv;

  if (!activeWorkspaceId) return null;

  const wsName = activeWs?.name || "Your Workspace";
  // The home feed takes the right-hand column on wide screens unless the Feed or the live data panel is open there
  const todayColumn = viewState === "main" && !historySearch && !showActivity && uiMode !== "power";
  const userName = user?.firstName || user?.lastName ? [user?.firstName, user?.lastName].filter(Boolean).join(' ') : 'there';

  return (
    <div
      className="flex flex-col h-full overflow-hidden relative"
      data-testid="draper-home-page"
      style={{
        background: "radial-gradient(ellipse 90% 55% at 16% 8%, rgba(29,78,216,0.13) 0%, transparent 55%), radial-gradient(ellipse 70% 50% at 84% 90%, rgba(109,40,217,0.09) 0%, transparent 55%), var(--background)",
      }}
    >
      {/* ── Header ── */}
      <div
        className="shrink-0 flex items-center gap-2 sm:gap-3 px-3 sm:px-5 h-14 border-b"
        style={{ background: "var(--chat-header-bg)", backdropFilter: "blur(16px)", borderColor: "var(--chat-header-border)" }}
      >
        {/* Left: workspace + status */}
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          {viewState === "project" && (
            <button
              onClick={() => { setViewState("main"); setActiveProjectId(null); }}
              className="text-white/30 hover:text-white/60 transition-colors shrink-0"
              data-testid="button-back-to-main"
            >
              <ChevronRight size={16} className="rotate-180" />
            </button>
          )}
          <AgentFace agent="draper" mood={draperMood} size={36} />
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h1 className="text-sm font-bold text-white truncate" data-testid="text-workspace-name">
                {viewState === "project" && activeProjectData ? activeProjectData.name : wsName}
              </h1>
              {viewState === "main" && activeWs && user && !pilotMode && (
                <span className="hidden sm:inline px-1.5 py-0.5 rounded text-[9px] font-black bg-blue-500/15 border border-blue-500/25 text-blue-300 uppercase tracking-wider shrink-0" data-testid="badge-plan">
                  {planLabel}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex items-center shrink-0" data-testid="team-faces">
                {(["peggy", "pete", "rogers", "harry"] as const).map((a, i) => (
                  <AgentFace key={a} agent={a} size={15} className={i ? "-ml-1" : ""} />
                ))}
                <span className="text-[10px] text-white/35 ml-1.5 truncate">
                  {sending || hasActiveJob ? "Draper is on it…" : "Draper and the team are here"}
                </span>
              </div>
              {telegramConnected && (
                <span className="hidden sm:flex items-center gap-0.5 text-sky-400/50 text-[9px]" data-testid="badge-telegram-connected">
                  <SiTelegram size={7} /> synced
                </span>
              )}
              {knowledgeRefresh?.lastRefreshedAt && (
                <span className="hidden md:flex items-center gap-0.5 text-violet-400/50 text-[9px]" data-testid="badge-knowledge-refresh">
                  <Telescope size={7} /> refreshed {timeAgo(knowledgeRefresh.lastRefreshedAt)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Center: draper status */}
        <div className="hidden md:flex items-center gap-2 shrink-0">
          <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold border transition-all ${
            sending || hasActiveJob ? "bg-blue-500/15 border-blue-500/30 text-blue-300" : "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
          }`}>
            <div className={`w-1.5 h-1.5 rounded-full ${sending || hasActiveJob ? "bg-blue-400 animate-pulse" : "bg-emerald-400"}`} />
            {sending ? "Thinking…" : hasActiveJob ? "Working…" : "Draper ready"}
          </div>
          <WorkerStatusChip status={workerStatus} className="hidden md:inline-flex" />
        </div>

        {/* Right: mode toggles */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Search history toggle */}
          <button
            onClick={() => { setShowHistorySearch(s => !s); setHistorySearch(''); }}
            data-testid="button-toggle-history-search"
            title="Search conversation"
            className={`w-8 h-8 rounded-xl flex items-center justify-center border transition-all ${
              showHistorySearch
                ? 'bg-blue-500/15 border-blue-500/30 text-blue-300'
                : 'bg-white/5 border-white/8 text-white/25 hover:text-white/50 hover:border-white/15'
            }`}
          >
            <Search size={14} />
          </button>

          {/* Activity feed toggle */}
          <button
            onClick={() => setShowActivity(s => !s)}
            data-testid="button-toggle-activity"
            className={`relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[10px] font-bold border transition-all ${
              showActivity
                ? "bg-amber-500/15 border-amber-500/30 text-amber-300"
                : "bg-white/5 border-white/8 text-white/25 hover:text-white/50 hover:border-white/15"
            }`}
            title="Activity feed"
          >
            <Activity size={12} />
            <span className="hidden sm:inline">Feed</span>
            {pendingCount > 0 && (
              <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-amber-500 text-[8px] font-black text-black flex items-center justify-center" data-testid="badge-activity-count">
                {pendingCount > 9 ? "9+" : pendingCount}
              </span>
            )}
          </button>

          {/* Right panel toggle */}
          <button
            onClick={() => toggleUiMode()}
            data-testid="button-toggle-ui-mode"
            className={`hidden md:flex w-8 h-8 rounded-xl items-center justify-center border transition-all ${
              uiMode === "power"
                ? "bg-violet-500/15 border-violet-500/30 text-violet-300"
                : "bg-white/5 border-white/8 text-white/25 hover:text-white/50 hover:border-white/15"
            }`}
            title={uiMode === "power" ? "Hide live panel" : "Show live data panel"}
          >
            <PanelRight size={14} />
          </button>

          {/* Projects toggle */}
          <button
            onClick={() => setShowProjectsList(s => !s)}
            data-testid="button-toggle-projects"
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-bold border transition-all ${
              showProjectsList ? "bg-blue-500/15 border-blue-500/30 text-blue-300" : "bg-white/5 border-white/10 text-white/35 hover:border-white/20"
            }`}
          >
            <FolderOpen size={11} />
            <span className="hidden sm:inline">Projects</span>
            {projectsData.length > 0 && <span className="ml-0.5">{projectsData.length}</span>}
            <ChevronDown size={9} className={`transition-transform ${showProjectsList ? "rotate-180" : ""}`} />
          </button>
        </div>
      </div>

      {/* An open P0 stays pinned under the header until it is acknowledged. */}
      <EscalationBanner className="shrink-0 px-3 pt-3" />

      {/* ── Projects dropdown ── */}
      {showProjectsList && (
        <div
          className="shrink-0 px-4 pt-2 pb-1 flex flex-col gap-0 border-b"
          style={{ background: "var(--chat-projects-bg)", borderColor: "var(--chat-header-border)" }}
        >
          <div className="flex items-center gap-2 px-1 pb-2 mb-1 border-b border-white/8">
            <Search size={10} className="text-white/25 shrink-0" />
            <input
              autoFocus
              value={projectSearch}
              onChange={e => setProjectSearch(e.target.value)}
              placeholder="Find project..."
              className="flex-1 bg-transparent text-[10px] text-white/60 placeholder:text-white/25 outline-none"
              data-testid="input-project-search"
            />
            {projectSearch && (
              <button
                onClick={() => setProjectSearch('')}
                className="text-white/20 hover:text-white/50 transition-colors"
                data-testid="button-clear-project-search"
              >
                <X size={9} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => { setViewState("main"); setActiveProjectId(null); setShowProjectsList(false); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-bold border transition-all ${
                viewState === "main" ? "bg-blue-500/15 border-blue-500/30 text-blue-300" : "bg-white/5 border-white/8 text-white/35 hover:border-white/20"
              }`}
              data-testid="button-main-thread"
            >
              <Brain size={10} /> Main Thread
            </button>
            {filteredProjects.map(p => (
              <div key={p.id} className="flex items-center gap-0.5">
                <button
                  onClick={() => openProject(p)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-bold border transition-all ${
                    viewState === "project" && activeProjectId === p.id ? "bg-blue-500/15 border-blue-500/30 text-blue-300" : "bg-white/5 border-white/8 text-white/35 hover:border-white/20"
                  }`}
                  data-testid={`button-project-${p.id}`}
                >
                  <FolderOpen size={10} /> {p.name}
                </button>
                <button
                  onClick={() => deleteProject.mutate(p.id)}
                  className="w-5 h-5 rounded flex items-center justify-center text-white/15 hover:text-red-400 hover:bg-red-500/10 transition-all"
                  data-testid={`button-delete-project-${p.id}`}
                  title="Delete"
                >
                  <Trash2 size={9} />
                </button>
              </div>
            ))}
            {filteredProjects.length === 0 && projectSearch.trim() && (
              <span className="text-[10px] text-white/25 px-1" data-testid="text-no-projects-match">
                No projects match "{projectSearch}"
              </span>
            )}
            <button
              onClick={() => setShowCreateProject(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-bold border border-dashed border-white/15 text-white/25 hover:border-blue-500/30 hover:text-blue-400 transition-all"
              data-testid="button-new-project"
            >
              <Plus size={10} /> New Project
            </button>
          </div>
        </div>
      )}

      {showHistorySearch && (
        <div
          className="shrink-0 px-4 py-2 flex items-center gap-2 border-b"
          style={{ background: 'var(--chat-projects-bg)', borderColor: 'var(--chat-header-border)' }}
        >
          <Search size={12} className="text-white/30 shrink-0" />
          <input
            autoFocus
            value={historySearch}
            onChange={e => setHistorySearch(e.target.value)}
            placeholder="Search conversation..."
            className="flex-1 bg-transparent text-xs text-white/70 placeholder:text-white/25 outline-none"
            data-testid="input-history-search"
          />
          {historySearch && (
            <span className="text-[10px] text-white/30 shrink-0">
              {displayMessages.length} result{displayMessages.length !== 1 ? 's' : ''}
            </span>
          )}
          {historySearch && (
            <button
              onClick={() => setHistorySearch('')}
              className="text-white/25 hover:text-white/50 transition-colors shrink-0"
              data-testid="button-clear-search"
            >
              <X size={12} />
            </button>
          )}
        </div>
      )}

      {/* ── Main body ── */}
      <div className="flex-1 flex overflow-hidden min-h-0 relative">
        {/* ── Chat area ── */}
        <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <WorkerDelayBanner status={workerStatus} />
          {/* Greeting, what the team did today, what waits for the owner, reminders coming up (wide screens: right column) */}
          {viewState === "main" && !historySearch && (
            <div className={showActivity ? "hidden" : todayColumn ? "lg:hidden" : ""}><TodayWithDraper
              key={activeWorkspaceId}
              wsId={activeWorkspaceId}
              firstName={userName.split(" ")[0]}
              mood={draperMood}
              feed={activityData?.feed ?? []}
              pendingCount={pendingCount}
              onOpenFeed={() => setShowActivity(true)}
            /></div>
          )}
          {/* After onboarding: what is left to set up (real state, exact links); hidden when done */}
          {viewState === "main" && <FinishSetupCard workspaceId={activeWorkspaceId} />}
          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-5 space-y-4 custom-scrollbar" data-testid="message-thread">
            {isLoading ? (
              <div className="flex justify-center py-16">
                <Loader2 size={20} className="animate-spin text-white/20" />
              </div>
            ) : displayMessages.length === 0 && historySearch ? (
              <div className="flex flex-col items-center justify-center h-full gap-3">
                <p className="text-sm text-white/25">No messages match <span className="text-white/40">"{historySearch}"</span></p>
              </div>
            ) : displayMessages.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full min-h-[300px] gap-6 text-center px-4">
                <AgentFace agent="draper" mood="happy" size={72} />
                <div>
                  <h2 className="text-xl font-bold text-white mb-1.5">
                    {viewState === "project" ? `${activeProjectData?.name || "Project"} session` : `Good to see you, ${userName.split(" ")[0]}.`}
                  </h2>
                  <p className="text-sm text-white/35 max-w-sm leading-relaxed">
                    {viewState === "project"
                      ? activeProjectData?.description || "Your focused agent session is ready."
                      : "I'm Draper, your marketing lead. Peggy, Pete, Rogers and Harry work with me, and you can reach all of us right here. What shall we work on?"}
                  </p>
                </div>
                {viewState === "main" && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg">
                      {QUICK_PROMPTS.map((q, i) => {
                        const Icon = q.icon;
                        return (
                          <button
                            key={i}
                            onClick={() => { setInput(q.text); inputRef.current?.focus(); }}
                            className="flex items-center gap-2.5 text-left px-4 py-3 rounded-xl text-xs text-white/45 hover:text-white/70 transition-all"
                            style={{ background: "var(--chat-empty-prompt-bg)", border: "1px solid var(--chat-empty-prompt-border)" }}
                            data-testid={`button-quick-prompt-${i}`}
                          >
                            <Icon size={12} className="shrink-0 text-white/25" />
                            {q.text}
                          </button>
                        );
                      })}
                    </div>
                    {dailyActivity && (
                      <div className="flex gap-3 w-full max-w-lg mb-3" data-testid="daily-activity-bar">
                        {[
                          { label: "Agent actions", value: dailyActivity.agentActions, color: "text-blue-400" },
                          { label: "Content", value: dailyActivity.contentCreated, color: "text-purple-400" },
                          { label: "Leads", value: dailyActivity.leadsCaptured, color: "text-green-400" },
                          { label: "Emails", value: dailyActivity.emailsSent, color: "text-amber-400" },
                        ].map(({ label, value, color }) => (
                          <div key={label} className="flex-1 rounded-xl p-2.5 text-center" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)" }}>
                            <div className={`text-lg font-bold ${color}`}>{value}</div>
                            <div className="text-[10px] text-white/35 mt-0.5">{label}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            ) : (
              displayMessages.map((msg, idx) => {
                const prevMsg = displayMessages[idx - 1];
                const showDateSep = !prevMsg || new Date(msg.ts).toDateString() !== new Date(prevMsg.ts).toDateString();
                const isStreaming = streamingMsgTs === msg.ts;
                const streamedText = isStreaming
                  ? msg.text.split(/\s+/).filter(Boolean).slice(0, streamedWordCount).join(" ")
                  : undefined;
                return (
                  <Fragment key={`${msg.ts}-${idx}`}>
                    {showDateSep && <DateSeparator dateLabel={getDateLabel(msg.ts)} />}
                    <MessageBubble
                      msg={{ ...msg, richCard: msg.richCard ? { ...msg.richCard, actionState: cardStates[msg.ts] || msg.richCard.actionState } : undefined }}
                      onCardAction={handleCardAction}
                      streamedText={streamedText}
                      isFailed={!!failedMsgs[msg.ts]}
                      errorMsg={failedMsgs[msg.ts]}
                      onRetry={() => handleRetry(msg.ts, msg.text)}
                    />
                  </Fragment>
                );
              })
            )}

            {briefLoading && (
              <div className="flex gap-2.5 items-center">
                <AgentFace agent="draper" mood="thinking" size={30} />
                <div className="px-4 py-2.5 rounded-2xl rounded-tl-sm flex items-center gap-2" style={{ background: "var(--chat-thinking-bg)", border: "1px solid var(--chat-thinking-border)" }}>
                  <Loader2 size={11} className="animate-spin text-blue-400/50" />
                  <span className="text-xs text-white/35">Putting your daily brief together…</span>
                </div>
              </div>
            )}

            {(sending || agentWorking || streamingLogs.length > 0) && !(hasActiveJob && !sending) && (
              <InlineThinkingStream logs={streamingLogs} sending={sending || agentWorking} recentMessages={displayMessages.slice(-5)} />
            )}

            <ChatJobBubbles
              jobs={threadJobs}
              onRetry={handleRetryJob}
              onDismiss={chatJobs.dismissJob}
              latestActivity={streamingLogs[streamingLogs.length - 1]?.action}
              engine={aiEngine}
            />
          </div>

          {/* ── Idle cursor ── */}
          {showIdleCursor && !sending && !hasActiveJob && displayMessages.length > 0 && (
            <div className="shrink-0 px-6 pb-1 flex items-center gap-2" data-testid="idle-cursor">
              <span className="text-[11px] text-white/30 tracking-wide">I'm here, keeping an eye on things. Ask me anything.</span>
              <span className="inline-block w-[7px] h-3.5 rounded-sm animate-pulse" style={{ background: "var(--chat-idle-cursor)" }} />
            </div>
          )}

          {/* ── Input bar ── */}
          <div className="shrink-0 px-5 pb-5 pt-3" style={{ borderTop: "1px solid var(--chat-input-topborder)" }}>
            {/* Hybrid: work handed to the Claude team that is still running */}
            <WorkInProgressStrip status={workerStatus} className="mb-3" />
            {/* Quick-action chips */}
            {displayMessages.length === 0 || !sending ? (
              <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="quick-chips">
                {QUICK_CHIPS.map(chip => {
                  const Icon = chip.icon;
                  return (
                    <button
                      key={chip.text}
                      onClick={() => { setInput(chip.text === "/ commands" ? "/" : chip.text); inputRef.current?.focus(); }}
                      className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium transition-all hover:border-white/25 hover:text-white/60"
                      style={{ background: "var(--chat-chip-bg)", border: "1px solid var(--chat-chip-border)", color: "var(--chat-chip-color)" }}
                      data-testid={`chip-${chip.text.toLowerCase().replace(/\s+/g, "-").replace(/\//g, "")}`}
                    >
                      <Icon size={9} />
                      {chip.text}
                    </button>
                  );
                })}
              </div>
            ) : null}
            <div className="relative">
              {/* @mention suggestions */}
              {showMentions && filteredMentions.length > 0 && (
                <div className="absolute bottom-full left-0 mb-2 rounded-xl shadow-xl z-50 py-1 min-w-48 overflow-hidden" style={{ background: "var(--chat-mention-bg)", border: "1px solid var(--chat-mention-border)" }} data-testid="mention-suggestions">
                  {filteredMentions.map(mention => {
                    const voice = mention.slice(1).toLowerCase() as AgentVoice;
                    const meta = AGENT_META[voice];
                    const Icon = meta?.icon;
                    return (
                      <button key={mention} onClick={() => insertMention(mention)}
                        className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-white/5 transition-colors text-left"
                        data-testid={`mention-option-${voice}`}>
                        {Icon && <Icon size={12} className={meta.color} />}
                        <span className={`text-xs font-bold ${meta?.color}`}>{mention}</span>
                        <span className="text-[10px] text-white/25 ml-auto">{meta?.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              <div
                className="flex items-end gap-2 rounded-2xl px-4 py-3 transition-all"
                style={{ background: "var(--chat-input-bg)", border: "1px solid var(--chat-input-border)" }}
              >
                <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileAttach}
                  accept=".txt,.md,.csv,.pdf,.jpg,.jpeg,.png,.gif,.webp" data-testid="input-file-upload" />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-white/20 hover:text-white/45 transition-colors shrink-0 mb-0.5"
                  data-testid="button-attach-file" title="Attach file"
                >
                  <Paperclip size={15} />
                </button>
                <button
                  onClick={handleMicClick}
                  disabled={sttLoading || sending}
                  className={`transition-colors shrink-0 mb-0.5 ${
                    isRecording ? 'text-red-400 animate-pulse' : 'text-white/20 hover:text-white/45'
                  } disabled:text-white/10`}
                  data-testid="button-voice-input"
                  title={isRecording ? 'Stop recording' : 'Voice input'}
                >
                  {sttLoading ? <Loader2 size={15} className="animate-spin" /> : isRecording ? <MicOff size={15} /> : <Mic size={15} />}
                </button>
                <textarea
                  ref={inputRef} value={input} onChange={handleInputChange} onKeyDown={handleKeyDown}
                  placeholder={
                    viewState === "project" && activeProjectData
                      ? `Message ${activeProjectData.agentCast.map(a => AGENT_META[a as AgentVoice]?.label).filter(Boolean).join(", ")}... (@mention to direct)`
                      : "Ask Draper anything… or type @ for Peggy, Pete, Rogers or Harry"
                  }
                  rows={1} className="flex-1 bg-transparent text-sm text-white/80 placeholder:text-white/25 outline-none resize-none max-h-32"
                  data-testid="input-chat-message"
                  style={{ lineHeight: "1.5" }}
                />
                <button
                  onClick={handleSend} disabled={!input.trim() || sending}
                  className="p-1.5 text-blue-400 disabled:text-white/15 hover:text-blue-300 transition-colors shrink-0"
                  data-testid="button-send-message"
                >
                  {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                </button>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-1.5 px-1">
              <p className="text-[9px] text-white/18 flex-1">@ mention an agent · Shift+Enter for new line · drag to attach files</p>
              <div className="flex items-center gap-2">
                {draperMode === "power" && (
                  <span className="text-[9px] text-amber-400/50 flex items-center gap-1 border border-amber-500/15 rounded px-1.5 py-0.5" data-testid="badge-power-mode">
                    <Zap size={8} /> Power Mode
                  </span>
                )}
                {viewState === "main" && draperMessages.length > 0 && (
                  <button
                    onClick={async () => {
                      if (!confirm("Clear all conversation history?")) return;
                      await fetch(`/api/workspaces/${activeWorkspaceId}/draper-conversation/clear`, { method: "POST", credentials: "include" });
                      setDraperMessages([]);
                    }}
                    className="text-[9px] text-white/18 hover:text-white/40 transition-colors flex items-center gap-1"
                    data-testid="button-clear-history"
                  >
                    <RefreshCw size={8} /> Clear
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Activity Feed right panel ── */}
        {showActivity && activeWorkspaceId && (
          <div
            className="absolute inset-0 z-20 sm:static sm:z-auto shrink-0 sm:w-80 flex flex-col border-l overflow-hidden"
            style={{ borderColor: "var(--chat-surface-border)", background: "var(--chat-surface-bg)" }}
            data-testid="activity-panel"
          >
            {/* Phones: the feed covers the chat, so it needs its own way back */}
            <button
              onClick={() => setShowActivity(false)}
              className="sm:hidden absolute top-2.5 right-3 z-10 w-7 h-7 rounded-lg flex items-center justify-center text-white/50 hover:text-white/80 bg-white/5"
              aria-label="Close feed"
              data-testid="button-close-activity"
            >
              <X size={14} />
            </button>
            <ActivityFeedPanel wsId={activeWorkspaceId} />
          </div>
        )}

        {/* ── Home feed column (wide screens), when no other right panel is open ── */}
        {todayColumn && (
          <aside
            className="hidden lg:flex shrink-0 w-80 flex-col border-l overflow-hidden"
            style={{ borderColor: "var(--chat-surface-border)", background: "var(--chat-surface-bg)" }}
          >
            <TodayWithDraper
              key={activeWorkspaceId}
              variant="column"
              wsId={activeWorkspaceId}
              firstName={userName.split(" ")[0]}
              mood={draperMood}
              feed={activityData?.feed ?? []}
              pendingCount={pendingCount}
              onOpenFeed={() => setShowActivity(true)}
            />
          </aside>
        )}
        {/* ── Power mode right panel — only shown when toggle is active ── */}
        {!showActivity && uiMode === "power" && activeWorkspaceId && (
          <div
            className="shrink-0 w-72 flex flex-col gap-3 p-4 overflow-y-auto custom-scrollbar border-l"
            style={{ borderColor: "var(--chat-surface-border)", background: "var(--chat-surface-bg)" }}
            data-testid="power-panel"
          >
            <div className="flex items-center gap-2 mb-1">
              <LayoutGrid size={12} className="text-white/30" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-white/25">Pinned Views</span>
            </div>
            <ContentCalendarPanel wsId={activeWorkspaceId} />
            <LeadPipelinePanel wsId={activeWorkspaceId} />
            <AdSpendPanel wsId={activeWorkspaceId} />
            <div className="text-[9px] text-white/15 text-center mt-1">
              Draper adds panels as you work — or ask for any view.
            </div>
          </div>
        )}
      </div>

      {/* ── Modals ── */}
      {showCreateProject && (
        <CreateProjectModal
          onClose={() => setShowCreateProject(false)}
          onCreated={(p) => {
            setShowCreateProject(false);
            queryClient.invalidateQueries({ queryKey: ["/api/workspaces", activeWorkspaceId, "draper-projects"] });
            openProject(p);
          }}
        />
      )}
    </div>
  );
}
